import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import SelectCliente from "../components/ui/selectCliente";
import { api } from "../api";
import { showInfo, showError } from "../utils/toast";
import "../sass/pages/fiscalNfse.scss";

const FISCAL_NFSE_BASE = "/fiscal/nfse";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "VALIDADA", label: "Validada" },
  { value: "XML_GERADO", label: "XML gerado" },
  { value: "EMITINDO", label: "Emitindo" },
  { value: "AUTORIZADA", label: "Autorizada" },
  { value: "REJEITADA", label: "Rejeitada" },
  { value: "ERRO", label: "Erro" },
  { value: "CANCELADA", label: "Cancelada" },
];

const SITUACAO_OPTIONS = [
  { value: "Q", label: "Pagas" },
  { value: "P", label: "Em aberto" },
  { value: "TODAS", label: "Todas" },
];

const MODO_SELECAO_OPTIONS = [
  { value: "MAXIMO", label: "Maximizar valor do lote" },
  { value: "ANTIGO", label: "Priorizar vencimento antigo" },
];

const STATUS_META = {
  PENDENTE: { label: "Pendente", tone: "warning" },
  VALIDADA: { label: "Validada", tone: "success" },
  XML_GERADO: { label: "XML gerado", tone: "info" },
  EMITINDO: { label: "Emitindo", tone: "info" },
  AUTORIZADA: { label: "Autorizada", tone: "success" },
  REJEITADA: { label: "Rejeitada", tone: "danger" },
  ERRO: { label: "Erro", tone: "danger" },
  CANCELADA: { label: "Cancelada", tone: "neutral" },
};

const STATUS_EMISSAO_PERMITIDA = ["VALIDADA", "XML_GERADO", "REJEITADA", "ERRO"];
const STATUS_VALIDACAO_LOTE_PERMITIDA = ["PENDENTE"];
const STATUS_EMISSAO_LOTE_PERMITIDA = ["VALIDADA", "XML_GERADO", "REJEITADA", "ERRO"];
const STATUS_PROCESSAMENTO_LOTE_PERMITIDA = [
  ...new Set([...STATUS_VALIDACAO_LOTE_PERMITIDA, ...STATUS_EMISSAO_LOTE_PERMITIDA]),
];

const competenciaAtual = () => {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
};

const moedaBR = (valor) => {
  const n = Number(valor || 0);

  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const decimalBR = (valor) => {
  const n = Number(valor || 0);

  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const dataBR = (value) => {
  if (!value) return "—";

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);

  return dt.toLocaleDateString("pt-BR");
};

const dataHoraBR = (value) => {
  if (!value) return "—";

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);

  return dt.toLocaleString("pt-BR");
};

const formatarDuracaoMs = (ms) => {
  const totalSegundos = Math.max(0, Math.floor(Number(ms || 0) / 1000));
  const horas = Math.floor(totalSegundos / 3600);
  const minutos = Math.floor((totalSegundos % 3600) / 60);
  const segundos = totalSegundos % 60;

  if (horas > 0) {
    return `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}:${String(segundos).padStart(2, "0")}`;
  }

  return `${String(minutos).padStart(2, "0")}:${String(segundos).padStart(2, "0")}`;
};

const delay = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));


const onlyNumbers = (value) => String(value || "").replace(/\D/g, "");

const parseBooleanFlag = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;

  const text = String(value || "").trim().toUpperCase();

  return ["S", "SIM", "TRUE", "1", "OBRIGATORIO", "OBRIGATÓRIO"].includes(text);
};

const normalizeBoolean = (value, fallback = false) => {
  if (value === "" || value === undefined || value === null) return fallback;
  return parseBooleanFlag(value);
};

const booleanToBit = (value) => (value ? 1 : 0);

const getSessionValue = (keys = [], fallback = "") => {
  for (const key of keys) {
    const value =
      window.sessionStorage?.getItem(key) ||
      window.localStorage?.getItem(key);

    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim();
    }
  }

  return fallback;
};

const getClienteRaw = (cliente) => cliente?.data || cliente || {};

const getClienteCodigo = (cliente) => {
  const raw = getClienteRaw(cliente);

  return String(
    raw.CodigoCad ||
      raw.codigoCad ||
      raw.codigo ||
      raw.ChvCad ||
      raw.chvCad ||
      raw.value ||
      "",
  ).trim();
};

const getClienteNome = (cliente) => {
  const raw = getClienteRaw(cliente);

  return (
    raw.NomeFant ||
    raw.NomeFantasia ||
    raw.nomeFantasia ||
    raw.RazaoSocial ||
    raw.razaoSocial ||
    raw.nome ||
    raw.label ||
    "Cliente"
  );
};

const getStatusLabel = (status) => {
  const key = String(status || "").toUpperCase();

  return STATUS_META[key]?.label || key || "Indefinido";
};

const getStatusClass = (status) => {
  const key = String(status || "").toUpperCase();
  const tone = STATUS_META[key]?.tone || "neutral";

  return `status-chip status-${tone}`;
};

const normalizarStatus = (status) => String(status || "").trim().toUpperCase();

const normalizarTextoConfirmacao = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

const isStatusAutorizado = (status) => normalizarStatus(status) === "AUTORIZADA";

const isStatusCancelado = (status) => ["CANCELADA", "EXCLUIDA", "EXCLUÍDA"].includes(normalizarStatus(status));

const isStatusValidavelLote = (status) =>
  STATUS_VALIDACAO_LOTE_PERMITIDA.includes(normalizarStatus(status));

const isStatusEmitivelLote = (status) =>
  STATUS_EMISSAO_LOTE_PERMITIDA.includes(normalizarStatus(status));

const isStatusProcessavelLote = (status) =>
  STATUS_PROCESSAMENTO_LOTE_PERMITIDA.includes(normalizarStatus(status));

const getIdPreNfe = (item = {}) => {
  const value =
    item.idPreNfe ||
    item.idPreNfse ||
    item.ID_PRE_NFE ||
    item.ID_PRE_NFSE ||
    item.id ||
    item.ID;

  const numero = Number(value);
  return Number.isInteger(numero) && numero > 0 ? numero : null;
};

const getNomePreNota = (item = {}) =>
  item.cliente ||
  item.razaoSocial ||
  item.nomeCliente ||
  item.nomeFantasia ||
  `Pré-NFS-e #${getIdPreNfe(item) || "—"}`;


const montarResultadoOperacao = ({ idPreNfe, item = {}, etapa = "VALIDAÇÃO", ok = false, mensagem = "", status = "", extra = {} }) => ({
  idPreNfe: Number(idPreNfe || getIdPreNfe(item) || 0),
  nome: getNomePreNota(item || { idPreNfe }),
  cliente: item?.cliente || item?.razaoSocial || item?.nomeCliente || "",
  codCliente: item?.codCliente || item?.nrLicenca || item?.codigoCliente || "",
  cnpj: item?.cnpj || item?.cpfCnpj || item?.CpfCnpjLimpo || "",
  etapa,
  ok: Boolean(ok),
  status: status || item?.status || "",
  mensagem: mensagem || (ok ? "Processado com sucesso." : "Falha no processamento."),
  ...extra,
});


const getFirstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return null;
};

const isObrigatorio = (item = {}) => {
  return (
    parseBooleanFlag(item.obrigatorio) ||
    parseBooleanFlag(item.emissaoObrigatoria) ||
    parseBooleanFlag(item.nfseObrigatoria) ||
    parseBooleanFlag(item.clienteObrigatorio) ||
    parseBooleanFlag(item.obrigatorioNfse) ||
    String(item.tipoRegra || "").toUpperCase() === "OBRIGATORIO" ||
    String(item.tipoRegra || "").toUpperCase() === "OBRIGATÓRIO" ||
    String(item.origemRegra || "").toUpperCase() === "OBRIGATORIO" ||
    String(item.origemRegra || "").toUpperCase() === "OBRIGATÓRIO" ||
    String(item.fonteEmissao || "").toUpperCase() === "OBRIGATORIO" ||
    String(item.fonteEmissao || "").toUpperCase() === "OBRIGATÓRIO"
  );
};

const getRegraLabel = (item = {}) => {
  if (isObrigatorio(item)) return "Obrigatório";
  if (item.selecionadaLote || item.selecionadaParaPreNfse) return "No lote";
  if (item.elegivel === true) return "Elegível";
  return "Bloqueado";
};

const getRegraClass = (item = {}) => {
  if (isObrigatorio(item)) return "regra-chip regra-obrigatorio";
  if (item.selecionadaLote || item.selecionadaParaPreNfse) return "regra-chip regra-lote";
  if (item.elegivel === true) return "regra-chip regra-range";
  return "regra-chip regra-bloqueado";
};

const getMotivoLoteLabel = (value) => {
  const key = String(value || "").toUpperCase();

  const map = {
    CLIENTE_OBRIGATORIO: "Cliente obrigatório",
    COMPLEMENTO_LOTE: "Complemento do lote",
    COMPLEMENTO_LOTE_GAP: "Fechamento de sobra",
    SELECAO_MANUAL: "Seleção manual",
  };

  return map[key] || (key ? key : "—");
};

const getConfigValue = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) {
      return obj[key];
    }
  }

  return "";
};

const CONFIG_DEFAULT = {
  idConfig: "",
  nrLicenca: getSessionValue(["NR_LICENCA", "Nlicenca", "nrLicenca", "licenca"], "19118"),
  idEmpresa: Number(getSessionValue(["ID_EMPRESA", "Numero_Empresa", "idEmpresa"], "1")) || 1,

  ambiente: "HOMOLOGACAO",
  municipioIbge: "3556503",
  municipioNome: "Várzea Paulista",

  endpointProducao: "https://ws.nfe-cidades.com.br/ws/varzea",
  endpointHomologacao: "https://ws.homolog.nfe-cidades.com.br/ws/varzea",

  cnpjPrestador: "02823222000130",
  inscricaoMunicipal: "302646",
  codigoMobiliario: "000346",

  serieRps: "NFe",
  tipoRps: 1,
  proximoNumeroRps: 1,

  itemListaServico: "01.04.01",
  codigoNbs: "115021000",
  codigoCnae: "",
  codigoTributacaoMunicipio: "",

  exigibilidadeIss: 1,
  issRetido: false,
  aliquotaIss: "3.3600",

  optanteSimplesNacional: true,
  incentivoFiscal: false,

  discriminacaoPadrao: "Nota fiscal referente ao mês de {MES_EXTENSO} de {ANO}.",
  outrasInformacoes: "",

  certificadoId: "",
  certificadoNome: "",
  certificadoValidade: "",
  certificadoVinculado: false,

  emailRelatorioInterno: "financeiro@santsystem.com.br",
  enviarEmailCliente: true,
  enviarRelatorioInterno: true,

  ativo: true,
};

const normalizeConfigNfse = (payload = {}) => {
  const raw = payload?.data || payload?.config || payload || {};

  const certificado =
    raw.certificado ||
    raw.certificadoAtual ||
    raw.certificadoEmissor ||
    raw.Certificado ||
    {};

  return {
    ...CONFIG_DEFAULT,
    idConfig: getConfigValue(raw, "idConfig", "ID_CONFIG") || "",
    nrLicenca: String(getConfigValue(raw, "nrLicenca", "NR_LICENCA") || CONFIG_DEFAULT.nrLicenca),
    idEmpresa: Number(getConfigValue(raw, "idEmpresa", "ID_EMPRESA") || CONFIG_DEFAULT.idEmpresa),

    ambiente: String(getConfigValue(raw, "ambiente", "AMBIENTE") || CONFIG_DEFAULT.ambiente).toUpperCase(),
    municipioIbge: String(getConfigValue(raw, "municipioIbge", "MUNICIPIO_IBGE") || CONFIG_DEFAULT.municipioIbge),
    municipioNome: String(getConfigValue(raw, "municipioNome", "MUNICIPIO_NOME") || CONFIG_DEFAULT.municipioNome),

    endpointProducao: String(getConfigValue(raw, "endpointProducao", "ENDPOINT_PRODUCAO") || CONFIG_DEFAULT.endpointProducao),
    endpointHomologacao: String(getConfigValue(raw, "endpointHomologacao", "ENDPOINT_HOMOLOGACAO") || CONFIG_DEFAULT.endpointHomologacao),

    cnpjPrestador: onlyNumbers(getConfigValue(raw, "cnpjPrestador", "CNPJ_PRESTADOR") || CONFIG_DEFAULT.cnpjPrestador),
    inscricaoMunicipal: String(getConfigValue(raw, "inscricaoMunicipal", "INSCRICAO_MUNICIPAL") || CONFIG_DEFAULT.inscricaoMunicipal),
    codigoMobiliario: String(getConfigValue(raw, "codigoMobiliario", "CODIGO_MOBILIARIO") || CONFIG_DEFAULT.codigoMobiliario),

    serieRps: String(getConfigValue(raw, "serieRps", "SERIE_RPS") || CONFIG_DEFAULT.serieRps),
    tipoRps: Number(getConfigValue(raw, "tipoRps", "TIPO_RPS") || CONFIG_DEFAULT.tipoRps),
    proximoNumeroRps: Number(getConfigValue(raw, "proximoNumeroRps", "PROXIMO_NUMERO_RPS") || CONFIG_DEFAULT.proximoNumeroRps),

    itemListaServico: String(getConfigValue(raw, "itemListaServico", "ITEM_LISTA_SERVICO") || CONFIG_DEFAULT.itemListaServico),
    codigoNbs: onlyNumbers(getConfigValue(raw, "codigoNbs", "CODIGO_NBS") || CONFIG_DEFAULT.codigoNbs),
    codigoCnae: String(getConfigValue(raw, "codigoCnae", "CODIGO_CNAE") || ""),
    codigoTributacaoMunicipio: String(getConfigValue(raw, "codigoTributacaoMunicipio", "CODIGO_TRIBUTACAO_MUNICIPIO") || ""),

    exigibilidadeIss: Number(getConfigValue(raw, "exigibilidadeIss", "EXIGIBILIDADE_ISS") || CONFIG_DEFAULT.exigibilidadeIss),
    issRetido: normalizeBoolean(getConfigValue(raw, "issRetido", "ISS_RETIDO"), false),
    aliquotaIss: String(getConfigValue(raw, "aliquotaIss", "ALIQUOTA_ISS") || CONFIG_DEFAULT.aliquotaIss),

    optanteSimplesNacional: normalizeBoolean(
      getConfigValue(raw, "optanteSimplesNacional", "OPTANTE_SIMPLES_NACIONAL", "simplesNacional"),
      CONFIG_DEFAULT.optanteSimplesNacional,
    ),
    incentivoFiscal: normalizeBoolean(getConfigValue(raw, "incentivoFiscal", "INCENTIVO_FISCAL"), false),

    discriminacaoPadrao: String(getConfigValue(raw, "discriminacaoPadrao", "DISCRIMINACAO_PADRAO") || CONFIG_DEFAULT.discriminacaoPadrao),
    outrasInformacoes: String(getConfigValue(raw, "outrasInformacoes", "OUTRAS_INFORMACOES") || ""),

    certificadoId: getConfigValue(raw, "certificadoId", "CERTIFICADO_ID") || getConfigValue(certificado, "id", "ID") || "",
    certificadoNome: String(
      getConfigValue(certificado, "nome", "nomeCertificado", "NomeCertificado") ||
        getConfigValue(raw, "certificadoNome", "NomeCertificado") ||
        "",
    ),
    certificadoValidade:
      getConfigValue(certificado, "validade", "Validade") ||
      getConfigValue(raw, "certificadoValidade", "Validade") ||
      "",
    certificadoVinculado: Boolean(
      getConfigValue(raw, "certificadoId", "CERTIFICADO_ID") ||
        getConfigValue(certificado, "id", "ID") ||
        getConfigValue(raw, "certificadoVinculado"),
    ),

    emailRelatorioInterno: String(getConfigValue(raw, "emailRelatorioInterno", "EMAIL_RELATORIO_INTERNO") || CONFIG_DEFAULT.emailRelatorioInterno),
    enviarEmailCliente: normalizeBoolean(getConfigValue(raw, "enviarEmailCliente", "ENVIAR_EMAIL_CLIENTE"), CONFIG_DEFAULT.enviarEmailCliente),
    enviarRelatorioInterno: normalizeBoolean(getConfigValue(raw, "enviarRelatorioInterno", "ENVIAR_RELATORIO_INTERNO"), CONFIG_DEFAULT.enviarRelatorioInterno),

    ativo: normalizeBoolean(getConfigValue(raw, "ativo", "ATIVO"), CONFIG_DEFAULT.ativo),
  };
};

const validarEmail = (value) => {
  if (!value) return false;

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
};

const validarConfigNfse = (config) => {
  const erros = [];

  if (!config.nrLicenca) erros.push("NR Licença é obrigatório.");
  if (!Number(config.idEmpresa)) erros.push("ID Empresa é obrigatório.");

  if (!["HOMOLOGACAO", "PRODUCAO"].includes(String(config.ambiente).toUpperCase())) {
    erros.push("Ambiente inválido.");
  }

  if (onlyNumbers(config.cnpjPrestador).length !== 14) {
    erros.push("CNPJ do prestador deve possuir 14 dígitos.");
  }

  if (!config.inscricaoMunicipal) erros.push("Inscrição municipal é obrigatória.");
  if (!config.codigoMobiliario) erros.push("Código mobiliário é obrigatório.");
  if (!config.municipioIbge) erros.push("Município IBGE é obrigatório.");
  if (!config.itemListaServico) erros.push("Item da lista de serviço é obrigatório.");
  if (!config.codigoNbs) erros.push("Código NBS é obrigatório para o NFe-cidades.");
  if (!config.serieRps) erros.push("Série RPS é obrigatória.");
  if (!Number(config.tipoRps)) erros.push("Tipo RPS deve ser maior que zero.");
  if (!Number(config.proximoNumeroRps)) erros.push("Próximo número RPS deve ser maior que zero.");
  if (Number(config.aliquotaIss) < 0) erros.push("Alíquota ISS inválida.");

  if (config.enviarRelatorioInterno && !validarEmail(config.emailRelatorioInterno)) {
    erros.push("E-mail de relatório interno inválido.");
  }

  return erros;
};

const montarPayloadConfigNfse = (config) => ({
  idConfig: config.idConfig || undefined,
  nrLicenca: String(config.nrLicenca || "").trim(),
  idEmpresa: Number(config.idEmpresa || 1),
  ambiente: String(config.ambiente || "HOMOLOGACAO").toUpperCase(),

  municipioIbge: String(config.municipioIbge || "").trim(),
  municipioNome: String(config.municipioNome || "").trim(),

  endpointProducao: String(config.endpointProducao || "").trim(),
  endpointHomologacao: String(config.endpointHomologacao || "").trim(),

  cnpjPrestador: onlyNumbers(config.cnpjPrestador),
  inscricaoMunicipal: String(config.inscricaoMunicipal || "").trim(),
  codigoMobiliario: String(config.codigoMobiliario || "").trim(),

  serieRps: String(config.serieRps || "").trim(),
  tipoRps: Number(config.tipoRps || 1),
  proximoNumeroRps: Number(config.proximoNumeroRps || 1),

  itemListaServico: String(config.itemListaServico || "").trim(),
  codigoNbs: onlyNumbers(config.codigoNbs),
  codigoCnae: String(config.codigoCnae || "").trim() || null,
  codigoTributacaoMunicipio: String(config.codigoTributacaoMunicipio || "").trim() || null,

  exigibilidadeIss: Number(config.exigibilidadeIss || 1),
  issRetido: booleanToBit(config.issRetido),
  aliquotaIss: Number(config.aliquotaIss || 0),

  optanteSimplesNacional: booleanToBit(config.optanteSimplesNacional),
  incentivoFiscal: booleanToBit(config.incentivoFiscal),

  discriminacaoPadrao: String(config.discriminacaoPadrao || "").trim(),
  outrasInformacoes: String(config.outrasInformacoes || "").trim() || null,

  certificadoId: config.certificadoId ? Number(config.certificadoId) : null,

  emailRelatorioInterno: String(config.emailRelatorioInterno || "").trim() || null,
  enviarEmailCliente: booleanToBit(config.enviarEmailCliente),
  enviarRelatorioInterno: booleanToBit(config.enviarRelatorioInterno),

  ativo: booleanToBit(config.ativo),
});

const getAmbienteClass = (ambiente) => {
  const value = String(ambiente || "").toUpperCase();

  if (value === "PRODUCAO") return "ambiente-chip producao";
  if (value === "HOMOLOGACAO") return "ambiente-chip homologacao";

  return "ambiente-chip";
};

const getLoteStatusClass = (lote) => {
  if (!lote) return "lote-status neutral";
  if (lote.excedeuMaximo) return "lote-status danger";
  if (lote.abaixoMinimo) return "lote-status warning";
  if (lote.dentroRange) return "lote-status success";
  return "lote-status neutral";
};

const getLoteStatusLabel = (lote) => {
  if (!lote) return "Não montado";
  if (lote.excedeuMaximo) return "Excedeu o máximo";
  if (lote.abaixoMinimo) return "Abaixo do mínimo";
  if (lote.dentroRange) return "Dentro da meta";
  return "Montado";
};

const calcularPercentualLote = (valorSelecionado, valorMax) => {
  const selecionado = Number(valorSelecionado || 0);
  const maximo = Number(valorMax || 0);

  if (!maximo || maximo <= 0) return 0;

  return Math.max(0, Math.min(100, (selecionado / maximo) * 100));
};

const FiscalNfse = () => {
  const [aba, setAba] = useState("lote");
  const [clienteSelecionado, setClienteSelecionado] = useState(null);

  const [filtros, setFiltros] = useState({
    competencia: competenciaAtual(),
    valorMin: "10000",
    valorMax: "10500",
    valorAlvo: "",
    situacao: "Q",
    status: "",
    search: "",
    modoSelecao: "MAXIMO",
    considerarObrigatorios: true,
    somenteObrigatorios: false,
    obrigatoriosSempreEntram: true,
  });

  const [parcelas, setParcelas] = useState([]);
  const [lote, setLote] = useState(null);
  const [preNotas, setPreNotas] = useState([]);
  const [detalhe, setDetalhe] = useState(null);
  const [selecionadasDetalhe, setSelecionadasDetalhe] = useState(new Set());

  const [selectedTituloIds, setSelectedTituloIds] = useState(new Set());
  const [manualSelection, setManualSelection] = useState(false);

  const [parcelasResumo, setParcelasResumo] = useState({
    total: 0,
    totalParcelas: 0,
    totalElegiveis: 0,
    totalObrigatorios: 0,
    valorElegivel: 0,
    valorObrigatorio: 0,
  });

  const [preNotasResumo, setPreNotasResumo] = useState({
    total: 0,
  });

  const [loadingParcelas, setLoadingParcelas] = useState(false);
  const [loadingPreNotas, setLoadingPreNotas] = useState(false);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [salvandoParcelas, setSalvandoParcelas] = useState(false);
  const [validando, setValidando] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [validandoLote, setValidandoLote] = useState(false);
  const [emitindoLote, setEmitindoLote] = useState(false);
  const [automacaoResumo, setAutomacaoResumo] = useState(null);
  const [falhasLoteModalAberto, setFalhasLoteModalAberto] = useState(false);
  const [confirmacaoModal, setConfirmacaoModal] = useState(null);
  const confirmacaoResolverRef = useRef(null);
  const [progressoTick, setProgressoTick] = useState(Date.now());

  const [configNfse, setConfigNfse] = useState(CONFIG_DEFAULT);
  const [configOriginal, setConfigOriginal] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  const [configBusca, setConfigBusca] = useState({
    nrLicenca: CONFIG_DEFAULT.nrLicenca,
    idEmpresa: CONFIG_DEFAULT.idEmpresa,
  });

  const [filtroTabela, setFiltroTabela] = useState("selecionadas");

  const clienteId = useMemo(
    () => getClienteCodigo(clienteSelecionado),
    [clienteSelecionado],
  );

  const updateFiltro = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const atualizarConfigBusca = (campo, valor) => {
    setConfigBusca((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const atualizarConfigCampo = (campo, valor) => {
    setConfigNfse((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const abrirConfirmacaoOperacao = useCallback((options = {}) => {
    return new Promise((resolve) => {
      if (confirmacaoResolverRef.current) {
        confirmacaoResolverRef.current(false);
      }

      confirmacaoResolverRef.current = resolve;

      setConfirmacaoModal({
        tipo: options.tipo || "warning",
        titulo: options.titulo || "Confirmar operação",
        subtitulo: options.subtitulo || "Ação fiscal sensível",
        descricao: options.descricao || "Confirme para continuar.",
        alerta: options.alerta || "",
        textoObrigatorio: options.textoObrigatorio || "",
        confirmarLabel: options.confirmarLabel || "Confirmar",
        cancelarLabel: options.cancelarLabel || "Cancelar",
        valorDigitado: "",
        erro: "",
      });
    });
  }, []);

  const fecharConfirmacaoOperacao = useCallback((resultado = false) => {
    if (confirmacaoResolverRef.current) {
      confirmacaoResolverRef.current(Boolean(resultado));
      confirmacaoResolverRef.current = null;
    }

    setConfirmacaoModal(null);
  }, []);

  const atualizarTextoConfirmacao = (valor) => {
    setConfirmacaoModal((prev) => (
      prev
        ? {
            ...prev,
            valorDigitado: valor,
            erro: "",
          }
        : prev
    ));
  };

  const confirmarModalOperacao = useCallback(() => {
    if (!confirmacaoModal) return;

    const textoObrigatorio = String(confirmacaoModal.textoObrigatorio || "").trim();

    if (textoObrigatorio) {
      const digitado = normalizarTextoConfirmacao(confirmacaoModal.valorDigitado);
      const esperado = normalizarTextoConfirmacao(textoObrigatorio);

      if (digitado !== esperado) {
        setConfirmacaoModal((prev) => (
          prev
            ? {
                ...prev,
                erro: `Digite exatamente ${textoObrigatorio} para confirmar.`,
              }
            : prev
        ));
        return;
      }
    }

    fecharConfirmacaoOperacao(true);
  }, [confirmacaoModal, fecharConfirmacaoOperacao]);

  const limparFiltros = () => {
    setClienteSelecionado(null);
    setFiltros({
      competencia: competenciaAtual(),
      valorMin: "10000",
      valorMax: "10500",
      valorAlvo: "",
      situacao: "Q",
      status: "",
      search: "",
      modoSelecao: "MAXIMO",
      considerarObrigatorios: true,
      somenteObrigatorios: false,
      obrigatoriosSempreEntram: true,
    });
    setLote(null);
    setParcelas([]);
    setSelectedTituloIds(new Set());
    setManualSelection(false);
    setFiltroTabela("selecionadas");
  };

  const carregarConfigNfse = useCallback(async () => {
    const nrLicenca = String(configBusca.nrLicenca || "").trim();
    const idEmpresa = Number(configBusca.idEmpresa || 1);

    if (!nrLicenca || !idEmpresa) {
      showError("Informe NR Licença e ID Empresa para carregar a configuração.");
      return;
    }

    setLoadingConfig(true);

    try {
      const { data } = await api.get(`${FISCAL_NFSE_BASE}/config`, {
        params: {
          nrLicenca,
          idEmpresa,
        },
      });

      const normalizada = normalizeConfigNfse(data);

      setConfigNfse(normalizada);
      setConfigOriginal(normalizada);
      setConfigBusca({
        nrLicenca: normalizada.nrLicenca,
        idEmpresa: normalizada.idEmpresa,
      });
    } catch (error) {
      console.error("Erro ao carregar configuração NFS-e:", {
        status: error.response?.status,
        data: error.response?.data,
        error,
      });

      showError(
        error.response?.data?.message ||
          "Erro ao carregar configuração NFS-e.",
      );

      const fallback = {
        ...CONFIG_DEFAULT,
        nrLicenca,
        idEmpresa,
      };

      setConfigNfse(fallback);
      setConfigOriginal(null);
    } finally {
      setLoadingConfig(false);
    }
  }, [configBusca.nrLicenca, configBusca.idEmpresa]);

  const validarProntoParaProducao = (config) => {
    const erros = validarConfigNfse({
      ...config,
      ambiente: "PRODUCAO",
    });

    if (!String(config.endpointProducao || "").trim()) {
      erros.push("Endpoint de produção é obrigatório.");
    }

    if (!config.certificadoId) {
      erros.push("Certificado emissor não vinculado à configuração NFS-e.");
    }

    if (config.certificadoValidade) {
      const validade = new Date(config.certificadoValidade);

      if (!Number.isNaN(validade.getTime()) && validade < new Date()) {
        erros.push("Certificado emissor vencido. Vincule um certificado válido antes de ativar produção.");
      }
    }

    if (!Number(config.proximoNumeroRps || 0)) {
      erros.push("Próximo número RPS inválido.");
    }

    if (!config.ativo) {
      erros.push("A configuração precisa estar ativa para emissão em produção.");
    }

    return erros;
  };

  const confirmarAlteracaoParaProducao = async () => {
    return abrirConfirmacaoOperacao({
      tipo: "danger",
      subtitulo: "Alteração de ambiente fiscal",
      titulo: "Ativar emissão em produção?",
      descricao:
        "A partir desta alteração, as próximas NFS-e transmitidas terão valor fiscal real junto ao provedor NFe-cidades.",
      alerta:
        "Antes de confirmar, verifique certificado, próximo RPS, dados do prestador e endpoint de produção.",
      textoObrigatorio: "PRODUÇÃO",
      confirmarLabel: "Ativar produção",
      cancelarLabel: "Manter homologação",
    });
  };

  const confirmarRetornoParaHomologacao = async () => {
    return abrirConfirmacaoOperacao({
      tipo: "info",
      subtitulo: "Alteração de ambiente fiscal",
      titulo: "Retornar para homologação?",
      descricao:
        "As próximas transmissões serão feitas no ambiente de teste, sem valor fiscal real.",
      alerta:
        "Essa alteração não cancela NFS-e já emitidas em produção.",
      confirmarLabel: "Voltar para homologação",
      cancelarLabel: "Cancelar",
    });
  };

  const confirmarGeracaoPreNotaProducao = async () => {
    return abrirConfirmacaoOperacao({
      tipo: "warning",
      subtitulo: "Preparação para produção",
      titulo: "Gerar pré-NFS-e em produção?",
      descricao:
        "A geração da pré-NFS-e ainda não transmite para a prefeitura, mas prepara o lote para emissão fiscal real posteriormente.",
      alerta:
        "Confira a competência, clientes selecionados e valores antes de continuar.",
      textoObrigatorio: "PRODUÇÃO",
      confirmarLabel: "Gerar pré-NFS-e",
      cancelarLabel: "Cancelar",
    });
  };

  const confirmarEmissaoIndividualProducao = async (idPreNfe) => {
    return abrirConfirmacaoOperacao({
      tipo: "danger",
      subtitulo: "Emissão fiscal em produção",
      titulo: `Emitir a pré-NFS-e #${idPreNfe} em produção?`,
      descricao:
        "Esta operação transmite uma NFS-e com valor fiscal real para o provedor NFe-cidades.",
      alerta:
        "O backend bloqueará reenvio se esta pré-NFS-e já estiver autorizada.",
      textoObrigatorio: "EMITIR",
      confirmarLabel: "Emitir em produção",
      cancelarLabel: "Cancelar emissão",
    });
  };

  const salvarConfigNfse = async (configOverride = null, options = {}) => {
    const configParaSalvar = configOverride || configNfse;
    const ambienteAtual = String(configOriginal?.ambiente || "").toUpperCase();
    const ambienteNovo = String(configParaSalvar.ambiente || "").toUpperCase();
    const pulouConfirmacaoAmbiente = Boolean(options.pularConfirmacaoAmbiente);

    const erros = validarConfigNfse(configParaSalvar);

    if (erros.length > 0) {
      showError(erros[0]);
      return false;
    }

    if (
      ambienteNovo === "PRODUCAO" &&
      ambienteAtual !== "PRODUCAO" &&
      !pulouConfirmacaoAmbiente &&
      !(await confirmarAlteracaoParaProducao())
    ) {
      showError("Alteração para produção cancelada.");
      return false;
    }

    setSalvandoConfig(true);

    try {
      const payload = montarPayloadConfigNfse(configParaSalvar);

      const { data } = await api.put(`${FISCAL_NFSE_BASE}/config`, payload);
      const normalizada = normalizeConfigNfse(data);

      setConfigNfse(normalizada);
      setConfigOriginal(normalizada);
      setConfigBusca({
        nrLicenca: normalizada.nrLicenca,
        idEmpresa: normalizada.idEmpresa,
      });

      showInfo(options.mensagemSucesso || "Configuração NFS-e salva com sucesso.");
      return true;
    } catch (error) {
      console.error("Erro ao salvar configuração NFS-e:", {
        status: error.response?.status,
        data: error.response?.data,
        error,
      });

      showError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Erro ao salvar configuração NFS-e.",
      );
      return false;
    } finally {
      setSalvandoConfig(false);
    }
  };

  const alterarAmbienteNfse = async (novoAmbiente) => {
    const ambienteAlvo = String(novoAmbiente || "").toUpperCase();
    const ambienteAtual = String(configNfse?.ambiente || "").toUpperCase();

    if (!['HOMOLOGACAO', 'PRODUCAO'].includes(ambienteAlvo) || ambienteAlvo === ambienteAtual) {
      return;
    }

    const configAtualizada = {
      ...configNfse,
      ambiente: ambienteAlvo,
    };

    if (ambienteAlvo === "PRODUCAO") {
      const errosProducao = validarProntoParaProducao(configAtualizada);

      if (errosProducao.length > 0) {
        showError(errosProducao[0]);
        return;
      }

      if (!(await confirmarAlteracaoParaProducao())) {
        showError("Alteração para produção cancelada.");
        return;
      }
    }

    if (
      ambienteAlvo === "HOMOLOGACAO" &&
      !(await confirmarRetornoParaHomologacao())
    ) {
      return;
    }

    await salvarConfigNfse(configAtualizada, {
      pularConfirmacaoAmbiente: true,
      mensagemSucesso:
        ambienteAlvo === "PRODUCAO"
          ? "Ambiente NFS-e alterado para PRODUÇÃO."
          : "Ambiente NFS-e alterado para HOMOLOGAÇÃO.",
    });
  };

  const montarParamsParcelas = useCallback(() => {
    const params = {
      competencia: filtros.competencia,
      valorMin: filtros.valorMin || 0,
      valorMax: filtros.valorMax || 999999999,
      valorAlvo: filtros.valorAlvo || undefined,
      situacao: filtros.situacao,
      search: filtros.search || "",
      modoSelecao: filtros.modoSelecao || "MAXIMO",
      considerarObrigatorios: filtros.considerarObrigatorios,
      somenteObrigatorios: filtros.somenteObrigatorios,
      obrigatoriosSempreEntram: filtros.obrigatoriosSempreEntram,
      limit: 5000,
    };

    if (clienteId) {
      params.clienteId = clienteId;
    }

    return params;
  }, [filtros, clienteId]);

  const buscarParcelas = useCallback(async () => {
    if (!filtros.competencia) {
      showError("Informe a competência.");
      return;
    }

    if (Number(filtros.valorMax || 0) <= 0) {
      showError("Informe o valor máximo do lote.");
      return;
    }

    if (Number(filtros.valorMin || 0) > Number(filtros.valorMax || 0)) {
      showError("Valor mínimo não pode ser maior que o valor máximo.");
      return;
    }

    setLoadingParcelas(true);

    try {
      const { data } = await api.get(`${FISCAL_NFSE_BASE}/parcelas-elegiveis`, {
        params: montarParamsParcelas(),
      });

      const parcelasApi = Array.isArray(data?.data) ? data.data : [];
      const loteApi = data?.lote || null;
      const idsLote = Array.isArray(loteApi?.tituloIdsSelecionados)
        ? loteApi.tituloIdsSelecionados
        : parcelasApi
            .filter((p) => p.selecionadaLote || p.selecionadaParaPreNfse)
            .map((p) => Number(p.tituloId));

      setParcelas(parcelasApi);
      setLote(loteApi);
      setSelectedTituloIds(new Set(idsLote.map((id) => Number(id))));
      setManualSelection(false);
      setFiltroTabela("selecionadas");

      setParcelasResumo({
        total: Number(data?.total || 0),
        totalParcelas: Number(data?.totalParcelas || 0),
        totalElegiveis: Number(data?.totalElegiveis || 0),
        totalObrigatorios: Number(data?.totalObrigatorios || 0),
        valorElegivel: Number(data?.valorElegivel || 0),
        valorObrigatorio: Number(data?.valorObrigatorio || 0),
      });

      showInfo(loteApi?.message || "Lote de emissão montado com sucesso.");
    } catch (error) {
      console.error("Erro ao buscar parcelas elegíveis:", {
        status: error.response?.status,
        data: error.response?.data,
        error,
      });

      showError(
        error.response?.data?.message ||
          "Erro ao buscar parcelas elegíveis para NFS-e.",
      );
    } finally {
      setLoadingParcelas(false);
    }
  }, [
    filtros.competencia,
    filtros.valorMin,
    filtros.valorMax,
    montarParamsParcelas,
  ]);

  const buscarPreNotas = useCallback(async (options = {}) => {
    if (!filtros.competencia) return [];

    const atualizarEstado = options.atualizarEstado !== false;
    const silencioso = Boolean(options.silencioso);

    if (atualizarEstado) {
      setLoadingPreNotas(true);
    }

    try {
      const params = {
        competencia: filtros.competencia,
        limit: options.limit || 500,
      };

      if (options.statusOverride) {
        params.status = options.statusOverride;
      }

      if (clienteId) {
        params.clienteId = clienteId;
      }

      const { data } = await api.get(`${FISCAL_NFSE_BASE}/pre-notas`, { params });
      const lista = Array.isArray(data?.data) ? data.data : [];

      if (atualizarEstado) {
        setPreNotas(lista);
        setPreNotasResumo({ total: Number(data?.total || lista.length || 0) });
      }

      return lista;
    } catch (error) {
      console.error("Erro ao listar pré-NFS-e:", {
        status: error.response?.status,
        data: error.response?.data,
        error,
      });

      if (!silencioso) {
        showError(error.response?.data?.message || "Erro ao listar pré-NFS-e.");
      }

      return [];
    } finally {
      if (atualizarEstado) {
        setLoadingPreNotas(false);
      }
    }
  }, [filtros.competencia, clienteId]);

  useEffect(() => {
    buscarPreNotas();
  }, [buscarPreNotas]);

  useEffect(() => {
    carregarConfigNfse();
  }, [carregarConfigNfse]);

  useEffect(() => {
    const emAndamento = normalizarStatus(automacaoResumo?.status) === "EM_ANDAMENTO";

    if (!emAndamento) return undefined;

    const timer = window.setInterval(() => {
      setProgressoTick(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [automacaoResumo?.status]);

  useEffect(() => {
    const emAndamento = normalizarStatus(automacaoResumo?.status) === "EM_ANDAMENTO";

    if (!emAndamento) return undefined;

    const bloquearSaida = (event) => {
      event.preventDefault();
      event.returnValue = "Existe um lote de NFS-e em processamento.";
      return event.returnValue;
    };

    window.addEventListener("beforeunload", bloquearSaida);

    return () => window.removeEventListener("beforeunload", bloquearSaida);
  }, [automacaoResumo?.status]);

  const parcelasElegiveis = useMemo(() => {
    return parcelas.filter((parcela) => Boolean(parcela.elegivel));
  }, [parcelas]);


  const parcelasBloqueadas = useMemo(() => {
    return parcelas.filter((parcela) => !parcela.elegivel);
  }, [parcelas]);

  const selectedTituloIdsArray = useMemo(() => {
    return Array.from(selectedTituloIds)
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
  }, [selectedTituloIds]);

  const parcelasSelecionadas = useMemo(() => {
    return parcelas
      .filter((parcela) => selectedTituloIds.has(Number(parcela.tituloId)))
      .sort((a, b) => {
        const ordemA = Number(a.ordemLote || 999999);
        const ordemB = Number(b.ordemLote || 999999);

        if (ordemA !== ordemB) return ordemA - ordemB;

        return String(a.cliente || "").localeCompare(String(b.cliente || ""));
      });
  }, [parcelas, selectedTituloIds]);

  const parcelasDisponiveis = useMemo(() => {
    return parcelasElegiveis.filter((parcela) => !selectedTituloIds.has(Number(parcela.tituloId)));
  }, [parcelasElegiveis, selectedTituloIds]);

  const valorSelecionadoManual = useMemo(() => {
    return parcelasSelecionadas.reduce((acc, parcela) => acc + Number(parcela.valor || 0), 0);
  }, [parcelasSelecionadas]);

  const valorObrigatorioSelecionado = useMemo(() => {
    return parcelasSelecionadas
      .filter((parcela) => isObrigatorio(parcela))
      .reduce((acc, parcela) => acc + Number(parcela.valor || 0), 0);
  }, [parcelasSelecionadas]);

  const clientesSelecionados = useMemo(() => {
    const map = new Map();

    for (const parcela of parcelasSelecionadas) {
      const key = String(parcela.clienteId || "").trim();
      if (!key) continue;

      if (!map.has(key)) {
        map.set(key, {
          clienteId: key,
          cliente: parcela.cliente,
          razaoSocial: parcela.razaoSocial,
          cnpj: parcela.cnpj,
          obrigatorio: isObrigatorio(parcela),
          qtdParcelas: 0,
          valorTotal: 0,
        });
      }

      const item = map.get(key);
      item.qtdParcelas += 1;
      item.valorTotal += Number(parcela.valor || 0);

      if (isObrigatorio(parcela)) {
        item.obrigatorio = true;
      }
    }

    return Array.from(map.values())
      .map((item) => ({
        ...item,
        valorTotal: Number(item.valorTotal.toFixed(2)),
      }))
      .sort((a, b) => b.valorTotal - a.valorTotal);
  }, [parcelasSelecionadas]);

  const statusResumo = useMemo(() => {
    return preNotas.reduce((acc, item) => {
      const key = String(item.status || "INDEFINIDO").toUpperCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [preNotas]);

  const preNotasFiltradas = useMemo(() => {
    const statusFiltro = normalizarStatus(filtros.status);
    const search = String(filtros.search || "")
      .trim()
      .toUpperCase();

    return preNotas.filter((item) => {
      if (statusFiltro && normalizarStatus(item.status) !== statusFiltro) {
        return false;
      }

      if (!search) return true;

      const texto = [
        item.idPreNfe,
        item.idPreNfse,
        item.cliente,
        item.razaoSocial,
        item.codCliente,
        item.nrLicenca,
        item.cnpj,
        item.mensagemStatus,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toUpperCase();

      return texto.includes(search);
    });
  }, [preNotas, filtros.status, filtros.search]);

  const tabelaParcelas = useMemo(() => {
    if (filtroTabela === "selecionadas") return parcelasSelecionadas;
    if (filtroTabela === "disponiveis") return parcelasDisponiveis;
    if (filtroTabela === "obrigatorias") return parcelas.filter((p) => isObrigatorio(p));
    if (filtroTabela === "bloqueadas") return parcelasBloqueadas;
    return parcelas;
  }, [
    filtroTabela,
    parcelas,
    parcelasSelecionadas,
    parcelasDisponiveis,
    parcelasBloqueadas,
  ]);

  const loteCalculado = useMemo(() => {
    const valorMin = Number(filtros.valorMin || 0);
    const valorMax = Number(filtros.valorMax || 0);
    const valorSelecionado = Number(valorSelecionadoManual || 0);

    return {
      valorMin,
      valorMax,
      valorSelecionado,
      dentroRange: valorSelecionado >= valorMin && valorSelecionado <= valorMax,
      abaixoMinimo: valorSelecionado < valorMin,
      excedeuMaximo: valorMax > 0 && valorSelecionado > valorMax,
      faltaParaMinimo: Math.max(0, valorMin - valorSelecionado),
      saldoAteMaximo: Math.max(0, valorMax - valorSelecionado),
      percentual: calcularPercentualLote(valorSelecionado, valorMax),
    };
  }, [filtros.valorMin, filtros.valorMax, valorSelecionadoManual]);

  const podeGerarPreNotas = selectedTituloIdsArray.length > 0 && !loteCalculado.excedeuMaximo;

  const selecionarSugestaoAutomatica = () => {
    const ids = Array.isArray(lote?.tituloIdsSelecionados)
      ? lote.tituloIdsSelecionados.map((id) => Number(id))
      : parcelas
          .filter((p) => p.selecionadaLote || p.selecionadaParaPreNfse)
          .map((p) => Number(p.tituloId));

    setSelectedTituloIds(new Set(ids));
    setManualSelection(false);
    setFiltroTabela("selecionadas");
  };

  const toggleTituloLote = (parcela) => {
    const id = Number(parcela?.tituloId);
    if (!id || !parcela?.elegivel) return;

    setSelectedTituloIds((prev) => {
      const next = new Set(prev);
      const selecionada = next.has(id);

      if (
        selecionada &&
        filtros.obrigatoriosSempreEntram &&
        isObrigatorio(parcela)
      ) {
        showError("Cliente obrigatório não pode ser removido do lote com a regra atual.");
        return prev;
      }

      if (selecionada) {
        next.delete(id);
      } else {
        next.add(id);
      }

      setManualSelection(true);
      return next;
    });
  };

  const acompanharGeracaoPreNotasJob = async (jobId, { totalEstimado = 0, startedAt = Date.now() } = {}) => {
    if (!jobId) throw new Error("jobId da geração não retornado pela API.");

    let ultimoJob = null;

    for (let tentativa = 0; tentativa < 900; tentativa += 1) {
      const { data } = await api.get(`${FISCAL_NFSE_BASE}/pre-notas/gerar-job/${jobId}`);
      const job = data?.data || data || {};
      ultimoJob = job;

      const statusApi = normalizarStatus(job.status || "EM_ANDAMENTO");
      const statusResumo = statusApi === "ERRO"
        ? "INTERROMPIDO"
        : statusApi === "PARCIAL"
          ? "PARCIAL"
          : statusApi === "CONCLUIDO"
            ? "CONCLUIDO"
            : "EM_ANDAMENTO";
      const emAndamento = statusResumo === "EM_ANDAMENTO";
      const total = Number(job.total || totalEstimado || 0);
      const processados = Number(job.processados || 0);
      const sucesso = Number(job.sucesso || 0);
      const falha = Number(job.falha || 0);
      const falhasDetalhadas = Array.isArray(job.falhasDetalhadas) ? job.falhasDetalhadas : [];
      const resultadosDetalhados = Array.isArray(job.resultadosDetalhados) ? job.resultadosDetalhados : [];
      const ultimos = Array.isArray(job.ultimos) ? job.ultimos : resultadosDetalhados.slice(-6).reverse();
      const idsGeradas = Array.isArray(job.idsGeradas) ? job.idsGeradas : [];

      setAutomacaoResumo({
        etapa: "GERACAO",
        modoFluxo: "GERACAO_ASSINCRONA",
        faseAtual: 1,
        fasesTotal: 1,
        status: statusResumo,
        mostrarModal: true,
        mensagem: job.mensagem || (emAndamento ? "Gerando pré-NFS-e..." : "Geração de pré-NFS-e finalizada."),
        total,
        processados,
        pendentes: Math.max(0, total - processados),
        sucesso,
        falha,
        atualId: job.atualId || null,
        atualNome: job.atualNome || (emAndamento ? "Aguardando próximo cliente..." : "Geração finalizada"),
        idsGeradas,
        resultadosDetalhados,
        falhasDetalhadas,
        ultimos,
        startedAt,
        finishedAt: emAndamento ? null : Date.now(),
        jobId,
      });

      if (!emAndamento) {
        return job;
      }

      await delay(800);
    }

    return ultimoJob;
  };

  const gerarPreNotas = async () => {
    if (!filtros.competencia) {
      showError("Informe a competência.");
      return;
    }

    if (!podeGerarPreNotas) {
      showError("Monte um lote válido antes de gerar as pré-NFS-e.");
      return;
    }

    const ambiente = String(configNfse?.ambiente || "").toUpperCase();

    if (
      ambiente === "PRODUCAO" &&
      !(await confirmarGeracaoPreNotaProducao())
    ) {
      return;
    }

    setGerando(true);
    setProgressoTick(Date.now());

    const startedAt = Date.now();
    const totalEstimado = Math.max(1, clientesSelecionados.length || parcelasSelecionadas.length || 1);

    setAutomacaoResumo({
      etapa: "GERACAO",
      modoFluxo: "GERACAO_ASSINCRONA",
      faseAtual: 1,
      fasesTotal: 1,
      status: "EM_ANDAMENTO",
      mostrarModal: true,
      mensagem: "Preparando geração das pré-NFS-e...",
      total: totalEstimado,
      processados: 0,
      pendentes: totalEstimado,
      sucesso: 0,
      falha: 0,
      atualId: null,
      atualNome: "Preparando lote...",
      startedAt,
      ultimos: [],
      resultadosDetalhados: [],
      falhasDetalhadas: [],
    });

    try {
      const tituloIds = [...selectedTituloIdsArray];

      if (filtros.obrigatoriosSempreEntram) {
        for (const parcela of parcelasElegiveis) {
          if (isObrigatorio(parcela)) {
            const id = Number(parcela.tituloId);
            if (id && !tituloIds.includes(id)) {
              tituloIds.push(id);
            }
          }
        }
      }

      const payload = {
        competencia: filtros.competencia,
        valorMin: Number(filtros.valorMin || 0),
        valorMax: Number(filtros.valorMax || 999999999),
        valorAlvo: filtros.valorAlvo ? Number(filtros.valorAlvo) : null,
        situacao: filtros.situacao,
        search: filtros.search || "",
        modoSelecao: filtros.modoSelecao || "MAXIMO",
        considerarObrigatorios: filtros.considerarObrigatorios,
        somenteObrigatorios: filtros.somenteObrigatorios,
        obrigatoriosSempreEntram: filtros.obrigatoriosSempreEntram,
        tituloIds,
      };

      if (clienteId) {
        payload.clienteId = clienteId;
      }

      const { data } = await api.post(`${FISCAL_NFSE_BASE}/pre-notas/gerar-job`, payload);
      const jobInicial = data?.data || data || {};
      const jobId = jobInicial.jobId;

      if (!jobId) {
        throw new Error("A API não retornou o identificador do processamento de geração.");
      }

      const jobFinal = await acompanharGeracaoPreNotasJob(jobId, {
        totalEstimado,
        startedAt,
      });

      const totalGeradas = Number(
        jobFinal?.sucesso ||
          jobFinal?.resultado?.totalGeradas ||
          jobFinal?.resultado?.total ||
          0,
      );
      const falhas = Number(
        jobFinal?.falha ||
          (Array.isArray(jobFinal?.falhasDetalhadas) ? jobFinal.falhasDetalhadas.length : 0),
      );

      if (normalizarStatus(jobFinal?.status) === "ERRO") {
        showError(jobFinal?.mensagem || jobFinal?.erro?.message || "Erro ao gerar pré-NFS-e.");
      } else if (falhas > 0) {
        showError(`${totalGeradas} pré-NFS-e gerada(s), ${falhas} pendência(s). Clique em Ver falhas para conferir.`);
        setFalhasLoteModalAberto(true);
      } else if (totalGeradas > 0) {
        showInfo(`${totalGeradas} pré-NFS-e gerada(s) com sucesso.`);
      } else {
        showInfo(jobFinal?.mensagem || "Nenhuma pré-NFS-e foi gerada.");
      }

      await buscarParcelas();
      await buscarPreNotas();
      setAba("preNotas");
    } catch (error) {
      console.error("Erro ao gerar pré-NFS-e:", {
        status: error.response?.status,
        data: error.response?.data,
        error,
      });

      setAutomacaoResumo((prev) => ({
        ...(prev || {}),
        etapa: "GERACAO",
        status: "INTERROMPIDO",
        mostrarModal: true,
        mensagem:
          error.response?.data?.message ||
          error.message ||
          "Erro ao gerar pré-NFS-e.",
        finishedAt: Date.now(),
        atualId: null,
        atualNome: "Geração interrompida",
      }));

      showError(error.response?.data?.message || error.message || "Erro ao gerar pré-NFS-e.");
    } finally {
      setGerando(false);
    }
  };

  const abrirDetalhe = async (idPreNfe) => {
    if (!idPreNfe) return;

    setLoadingDetalhe(true);

    try {
      const { data } = await api.get(`${FISCAL_NFSE_BASE}/pre-notas/${idPreNfe}`);
      const item = data?.data || null;

      setDetalhe(item);

      const selecionadas = new Set(
        (item?.parcelas || [])
          .filter((p) => p.selecionada)
          .map((p) => Number(p.tituloId)),
      );

      setSelecionadasDetalhe(selecionadas);
    } catch (error) {
      console.error("Erro ao abrir detalhe da pré-NFS-e:", {
        status: error.response?.status,
        data: error.response?.data,
        error,
      });

      showError(error.response?.data?.message || "Erro ao abrir pré-NFS-e.");
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const fecharDetalhe = () => {
    setDetalhe(null);
    setSelecionadasDetalhe(new Set());
  };

  const toggleParcelaDetalhe = (tituloId) => {
    if (isStatusAutorizado(detalhe?.status) || isStatusCancelado(detalhe?.status)) return;

    const id = Number(tituloId);

    setSelecionadasDetalhe((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const valorSelecionadoDetalhe = useMemo(() => {
    return (detalhe?.parcelas || [])
      .filter((p) => selecionadasDetalhe.has(Number(p.tituloId)))
      .reduce((acc, p) => acc + Number(p.valor || 0), 0);
  }, [detalhe, selecionadasDetalhe]);

  const statusDetalhe = normalizarStatus(detalhe?.status);
  const detalheAutorizada = isStatusAutorizado(statusDetalhe);
  const detalheCancelada = isStatusCancelado(statusDetalhe);
  const detalheSomenteLeitura = detalheAutorizada || detalheCancelada;
  const podeEditarDetalhe = detalhe && !detalheSomenteLeitura;
  const podeValidarDetalhe = detalhe && !detalheSomenteLeitura;
  const podeEmitirDetalhe = detalhe && STATUS_EMISSAO_PERMITIDA.includes(statusDetalhe);

  const numeroNfseDetalhe = getFirstValue(
    detalhe?.numeroNfse,
    detalhe?.NUMERO_NFSE,
    detalhe?.numeroNota,
    detalhe?.nrNfse,
    detalhe?.nfseNumero,
  );

  const codigoVerificacaoDetalhe = getFirstValue(
    detalhe?.codigoVerificacao,
    detalhe?.CODIGO_VERIFICACAO,
    detalhe?.codigoVerificador,
    detalhe?.codVerificacao,
    detalhe?.verificacao,
  );

  const salvarParcelasDetalhe = async () => {
    if (!detalhe?.idPreNfe) return;

    if (detalheSomenteLeitura) {
      showInfo("Pré-NFS-e autorizada/cancelada é somente leitura.");
      return;
    }

    setSalvandoParcelas(true);

    try {
      await api.put(`${FISCAL_NFSE_BASE}/pre-notas/${detalhe.idPreNfe}/parcelas`, {
        tituloIdsSelecionados: Array.from(selecionadasDetalhe),
      });

      showInfo("Parcelas atualizadas. Valide novamente antes de emitir.");

      await abrirDetalhe(detalhe.idPreNfe);
      await buscarPreNotas();
    } catch (error) {
      console.error("Erro ao atualizar parcelas da pré-NFS-e:", {
        status: error.response?.status,
        data: error.response?.data,
        error,
      });

      showError(
        error.response?.data?.message ||
          "Erro ao atualizar parcelas da pré-NFS-e.",
      );
    } finally {
      setSalvandoParcelas(false);
    }
  };

  const validarPreNota = async (idPreNfe = detalhe?.idPreNfe) => {
    if (!idPreNfe) return;

    if (detalheSomenteLeitura && Number(idPreNfe) === Number(detalhe?.idPreNfe)) {
      showInfo("Pré-NFS-e autorizada/cancelada não pode ser validada novamente.");
      return;
    }

    setValidando(true);

    try {
      const { data } = await api.post(`${FISCAL_NFSE_BASE}/pre-notas/${idPreNfe}/validar`);

      if (data?.valid) {
        showInfo(data?.mensagem || "Pré-NFS-e validada com sucesso.");
      } else {
        showError(data?.mensagem || "Pré-NFS-e possui pendências.");
      }

      await abrirDetalhe(idPreNfe);
      await buscarPreNotas();
    } catch (error) {
      console.error("Erro ao validar pré-NFS-e:", {
        status: error.response?.status,
        data: error.response?.data,
        error,
      });

      showError(error.response?.data?.message || "Erro ao validar pré-NFS-e.");
    } finally {
      setValidando(false);
    }
  };

  const emitirPreNota = async (idPreNfe = detalhe?.idPreNfe) => {
    if (!idPreNfe) return;

    if (detalheAutorizada && Number(idPreNfe) === Number(detalhe?.idPreNfe)) {
      showInfo("Esta pré-NFS-e já está autorizada. Reemissão bloqueada.");
      return;
    }

    const ambiente = String(configNfse?.ambiente || "").toUpperCase();

    if (
      ambiente === "PRODUCAO" &&
      !(await confirmarEmissaoIndividualProducao(idPreNfe))
    ) {
      return;
    }

    setEmitindo(true);

    try {
      const { data } = await api.post(`${FISCAL_NFSE_BASE}/pre-notas/${idPreNfe}/emitir`, {
        transmitir: true,
        debugXml: false,
        timeoutMs: 90000,
        nrLicencaEmitente: configNfse.nrLicenca,
        idEmpresaEmitente: configNfse.idEmpresa,
      });

      showInfo(data?.message || "NFS-e enviada para emissão.");
      await abrirDetalhe(idPreNfe);
      await buscarPreNotas();
      await carregarConfigNfse();
    } catch (error) {
      const status = error.response?.status;
      const message =
        error.response?.data?.message ||
        "Erro ao emitir NFS-e.";

      console.error("Erro ao emitir NFS-e:", {
        status,
        data: error.response?.data,
        error,
      });

      if (status === 501) {
        showInfo(message);
      } else {
        showError(message);
      }
    } finally {
      setEmitindo(false);
    }
  };

  const validarPreNotaSilencioso = async (idPreNfe) => {
    try {
      const { data } = await api.post(`${FISCAL_NFSE_BASE}/pre-notas/validar-lote`, {
        competencia: filtros.competencia,
        idsPreNfe: [Number(idPreNfe)],
        status: "PENDENTE",
        limit: 1,
      });

      const resultado = Array.isArray(data?.resultados)
        ? data.resultados.find((item) => Number(item.idPreNfe) === Number(idPreNfe)) || data.resultados[0]
        : null;

      const validou = Boolean(resultado?.valid) || Number(data?.validadas || 0) > 0;

      return {
        idPreNfe,
        ok: Boolean(validou),
        data,
        status: resultado?.status || data?.status,
        mensagem:
          resultado?.mensagem ||
          data?.message ||
          (validou ? "Validada" : "Possui pendências"),
      };
    } catch (error) {
      return {
        idPreNfe,
        ok: false,
        error,
        mensagem:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erro ao validar pré-NFS-e no lote.",
      };
    }
  };

  const validarLotePreNotas = async ({ idsPreNfe = null, silencioso = false, modoFluxo = "VALIDACAO", faseAtual = 1, fasesTotal = 1 } = {}) => {
    const idsBase = Array.isArray(idsPreNfe) && idsPreNfe.length
      ? idsPreNfe
      : preNotas
          .filter((item) => isStatusValidavelLote(item.status))
          .map(getIdPreNfe)
          .filter(Boolean);

    const ids = Array.from(new Set(idsBase.map(Number).filter((id) => Number.isInteger(id) && id > 0)));

    if (ids.length === 0) {
      if (!silencioso) showInfo("Nenhuma pré-NFS-e pendente para validação automática.");
      return { total: 0, processados: 0, sucesso: 0, falha: 0, idsValidas: [], resultados: [], historico: [], falhasDetalhadas: [] };
    }

    const startedAt = Date.now();
    const mapaPreNotas = new Map(preNotas.map((item) => [getIdPreNfe(item), item]));
    const historico = [];

    setValidandoLote(true);
    setProgressoTick(Date.now());
    setAutomacaoResumo({
      etapa: "VALIDACAO",
      modoFluxo,
      faseAtual,
      fasesTotal,
      status: "EM_ANDAMENTO",
      mostrarModal: true,
      mensagem: `Etapa ${faseAtual}/${fasesTotal}: validando ${ids.length} pré-NFS-e...`,
      total: ids.length,
      processados: 0,
      pendentes: ids.length,
      sucesso: 0,
      falha: 0,
      atualId: ids[0],
      atualNome: getNomePreNota(mapaPreNotas.get(ids[0]) || { idPreNfe: ids[0] }),
      startedAt,
      ultimos: [],
      resultadosDetalhados: [],
      falhasDetalhadas: [],
    });

    const resultados = [];

    for (const id of ids) {
      const itemAtual = mapaPreNotas.get(id) || { idPreNfe: id };

      setAutomacaoResumo((prev) => ({
        ...prev,
        mensagem: `Etapa ${faseAtual}/${fasesTotal}: validando ${resultados.length}/${ids.length}`,
        atualId: id,
        atualNome: getNomePreNota(itemAtual),
      }));

      const resultado = await validarPreNotaSilencioso(id);
      resultados.push(resultado);

      const itemHistorico = montarResultadoOperacao({
        idPreNfe: id,
        item: itemAtual,
        etapa: "Validação",
        ok: resultado.ok,
        status: resultado.status || (resultado.ok ? "VALIDADA" : "PENDENTE"),
        mensagem: resultado.mensagem || (resultado.ok ? "Pré-NFS-e validada com sucesso." : "Falha na validação."),
      });

      historico.push(itemHistorico);

      const processados = resultados.length;
      const sucesso = resultados.filter((r) => r.ok).length;
      const falha = resultados.filter((r) => !r.ok).length;
      const falhasDetalhadas = historico.filter((item) => !item.ok);

      setAutomacaoResumo((prev) => ({
        ...prev,
        etapa: "VALIDACAO",
        modoFluxo,
        faseAtual,
        fasesTotal,
        status: "EM_ANDAMENTO",
        mensagem: `Etapa ${faseAtual}/${fasesTotal}: validando ${processados}/${ids.length}`,
        total: ids.length,
        processados,
        pendentes: Math.max(0, ids.length - processados),
        sucesso,
        falha,
        atualId: ids[processados] || null,
        atualNome: ids[processados]
          ? getNomePreNota(mapaPreNotas.get(ids[processados]) || { idPreNfe: ids[processados] })
          : "Finalizando validação...",
        ultimos: historico.slice(-6).reverse(),
        resultadosDetalhados: historico,
        falhasDetalhadas,
      }));
    }

    const idsValidasDoRetorno = resultados
      .filter((item) => item.ok)
      .map((item) => Number(item.idPreNfe))
      .filter(Boolean);

    const listaCompletaAtualizada = await buscarPreNotas({ atualizarEstado: false, silencioso: true });
    const idsValidasDaLista = listaCompletaAtualizada
      .filter((item) => ids.includes(getIdPreNfe(item)))
      .filter((item) => isStatusEmitivelLote(item.status) || normalizarStatus(item.status) === "VALIDADA")
      .map(getIdPreNfe)
      .filter(Boolean);

    const idsValidas = Array.from(new Set([...idsValidasDoRetorno, ...idsValidasDaLista]));
    const falhasDetalhadas = historico.filter((item) => !item.ok);

    const resumo = {
      total: ids.length,
      processados: ids.length,
      pendentes: 0,
      sucesso: idsValidas.length,
      falha: Math.max(0, ids.length - idsValidas.length),
      idsValidas,
      resultados,
      resultadosDetalhados: historico,
      falhasDetalhadas,
      ultimos: historico.slice(-6).reverse(),
      startedAt,
      finishedAt: Date.now(),
    };

    setAutomacaoResumo({
      etapa: "VALIDACAO",
      modoFluxo,
      faseAtual,
      fasesTotal,
      status: resumo.falha > 0 ? "PARCIAL" : "CONCLUIDO",
      mostrarModal: true,
      mensagem:
        resumo.falha > 0
          ? `Validação concluída: ${resumo.sucesso} aprovada(s), ${resumo.falha} com pendência.`
          : `Validação concluída: ${resumo.sucesso} pré-NFS-e aprovada(s).`,
      atualId: null,
      atualNome: resumo.falha > 0 ? "Validação concluída com pendências" : "Validação concluída com sucesso",
      ...resumo,
    });

    if (!silencioso) {
      if (resumo.falha > 0) {
        showError(`${resumo.sucesso} validada(s), ${resumo.falha} com pendência. Clique em Ver falhas para conferir.`);
        setFalhasLoteModalAberto(true);
      } else {
        showInfo(`${resumo.sucesso} pré-NFS-e validada(s) com sucesso.`);
      }
    }

    if (detalhe?.idPreNfe && ids.includes(Number(detalhe.idPreNfe))) {
      await abrirDetalhe(detalhe.idPreNfe);
    }

    await buscarPreNotas();
    setValidandoLote(false);
    return resumo;
  };

  const confirmarEmissaoLoteProducao = async (quantidade) => {
    const ambiente = String(configNfse?.ambiente || "").toUpperCase();

    if (ambiente !== "PRODUCAO") {
      return true;
    }

    return abrirConfirmacaoOperacao({
      tipo: "danger",
      subtitulo: "Emissão fiscal em produção",
      titulo: `Emitir ${quantidade} NFS-e em produção?`,
      descricao:
        "Esta operação transmite NFS-e com valor fiscal real. O processamento é sequencial e não deve ser interrompido.",
      alerta:
        "O backend bloqueará reenvio de pré-NFS-e já autorizada, mas confira a competência e o lote antes de confirmar.",
      textoObrigatorio: "EMITIR LOTE",
      confirmarLabel: "Emitir lote em produção",
      cancelarLabel: "Cancelar emissão",
    });
  };

  const emitirItemLoteSilencioso = async (idPreNfe, { validarPendentes = true } = {}) => {
    try {
      const { data } = await api.post(`${FISCAL_NFSE_BASE}/pre-notas/emitir-lote`, {
        competencia: filtros.competencia,
        idsPreNfe: [Number(idPreNfe)],
        limit: 1,
        transmitir: true,
        debugXml: false,
        timeoutMs: 90000,
        validarPendentes,
        nrLicencaEmitente: configNfse.nrLicenca,
        idEmpresaEmitente: configNfse.idEmpresa,
      });

      const resultado = Array.isArray(data?.resultados)
        ? data.resultados.find((item) => Number(item.idPreNfe) === Number(idPreNfe)) || data.resultados[0]
        : data;

      const autorizada =
        resultado?.autorizada === true ||
        normalizarStatus(resultado?.status) === "AUTORIZADA" ||
        Number(data?.autorizadas || 0) > 0;

      return {
        idPreNfe,
        ok: Boolean(autorizada),
        data,
        resultado,
        rpsConsumido: Boolean(resultado?.rpsConsumido),
        mensagem:
          resultado?.mensagem ||
          resultado?.message ||
          data?.message ||
          (autorizada ? "Autorizada" : "Retorno não autorizado"),
      };
    } catch (error) {
      return {
        idPreNfe,
        ok: false,
        error,
        mensagem:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erro ao emitir NFS-e no lote.",
      };
    }
  };

  const emitirLotePreNotas = async ({ idsPreNfe = null, validarAntes = true, silencioso = false } = {}) => {
    const listaAtualizada = await buscarPreNotas({ atualizarEstado: false, silencioso: true });
    const idsInformados = Array.isArray(idsPreNfe) && idsPreNfe.length
      ? new Set(idsPreNfe.map(Number).filter((id) => Number.isInteger(id) && id > 0))
      : null;

    const listaBase = listaAtualizada || [];
    const candidatosItens = listaBase
      .filter((item) => (idsInformados ? idsInformados.has(getIdPreNfe(item)) : isStatusProcessavelLote(item.status)))
      .filter((item) => !isStatusAutorizado(item.status) && !isStatusCancelado(item.status));

    const pendentesValidacao = candidatosItens
      .filter((item) => isStatusValidavelLote(item.status))
      .map(getIdPreNfe)
      .filter(Boolean);

    const emitiveisIniciais = candidatosItens
      .filter((item) => isStatusEmitivelLote(item.status))
      .map(getIdPreNfe)
      .filter(Boolean);

    const candidatos = Array.from(new Set(candidatosItens.map(getIdPreNfe).filter(Boolean)));

    if (candidatos.length === 0) {
      if (!silencioso) showInfo("Nenhuma pré-NFS-e disponível para validar/emitir no lote.");
      return { total: 0, sucesso: 0, falha: 0, resultados: [], falhasDetalhadas: [] };
    }

    if (!(await confirmarEmissaoLoteProducao(candidatos.length))) {
      showError("Emissão em lote cancelada.");
      return { total: candidatos.length, sucesso: 0, falha: candidatos.length, resultados: [], falhasDetalhadas: [] };
    }

    setEmitindoLote(true);

    let idsLiberadosParaEmissao = [...emitiveisIniciais];
    let falhasValidacao = [];
    let historicoValidacao = [];

    if (validarAntes && pendentesValidacao.length > 0) {
      const resumoValidacao = await validarLotePreNotas({
        idsPreNfe: pendentesValidacao,
        silencioso: true,
        modoFluxo: "VALIDAR_EMITIR",
        faseAtual: 1,
        fasesTotal: 2,
      });

      idsLiberadosParaEmissao = Array.from(new Set([
        ...idsLiberadosParaEmissao,
        ...(resumoValidacao.idsValidas || []),
      ]));
      falhasValidacao = Array.isArray(resumoValidacao.falhasDetalhadas)
        ? resumoValidacao.falhasDetalhadas
        : [];
      historicoValidacao = Array.isArray(resumoValidacao.resultadosDetalhados)
        ? resumoValidacao.resultadosDetalhados
        : [];
    }

    idsLiberadosParaEmissao = Array.from(new Set(idsLiberadosParaEmissao.map(Number).filter(Boolean)));

    if (idsLiberadosParaEmissao.length === 0) {
      const resumoSemEmissao = {
        etapa: "VALIDACAO",
        modoFluxo: "VALIDAR_EMITIR",
        faseAtual: 1,
        fasesTotal: 2,
        status: "PARCIAL",
        mostrarModal: true,
        mensagem: "Validação concluída, mas nenhuma pré-NFS-e ficou apta para emissão.",
        total: pendentesValidacao.length,
        processados: pendentesValidacao.length,
        pendentes: 0,
        sucesso: 0,
        falha: falhasValidacao.length,
        atualId: null,
        atualNome: "Nenhuma pré-NFS-e apta para emissão",
        resultados: [],
        resultadosDetalhados: historicoValidacao,
        falhasDetalhadas: falhasValidacao,
        ultimos: historicoValidacao.slice(-6).reverse(),
        startedAt: Date.now(),
        finishedAt: Date.now(),
      };

      setAutomacaoResumo(resumoSemEmissao);
      setEmitindoLote(false);
      await buscarPreNotas();
      if (falhasValidacao.length > 0) setFalhasLoteModalAberto(true);
      return resumoSemEmissao;
    }

    const listaPosValidacao = await buscarPreNotas({ atualizarEstado: false, silencioso: true });
    const mapaPreNotas = new Map((listaPosValidacao || []).map((item) => [getIdPreNfe(item), item]));
    const startedAt = Date.now();
    const historicoEmissao = [];

    setProgressoTick(Date.now());
    setAutomacaoResumo({
      etapa: "EMISSAO",
      modoFluxo: "VALIDAR_EMITIR",
      faseAtual: validarAntes && pendentesValidacao.length > 0 ? 2 : 1,
      fasesTotal: validarAntes && pendentesValidacao.length > 0 ? 2 : 1,
      status: "EM_ANDAMENTO",
      mostrarModal: true,
      mensagem: `Etapa ${validarAntes && pendentesValidacao.length > 0 ? "2/2" : "1/1"}: emitindo ${idsLiberadosParaEmissao.length} NFS-e validadas...`,
      total: idsLiberadosParaEmissao.length,
      processados: 0,
      pendentes: idsLiberadosParaEmissao.length,
      sucesso: 0,
      falha: 0,
      atualId: idsLiberadosParaEmissao[0],
      atualNome: getNomePreNota(mapaPreNotas.get(idsLiberadosParaEmissao[0]) || { idPreNfe: idsLiberadosParaEmissao[0] }),
      startedAt,
      ultimos: [],
      resultadosDetalhados: [],
      falhasDetalhadas: falhasValidacao,
    });

    const resultados = [];
    let ultimaFalhaNormalizada = "";
    let falhasConsecutivas = 0;
    let interrompido = false;
    let motivoInterrupcao = "";

    const normalizarMensagemFalha = (mensagem) =>
      String(mensagem || "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 250)
        .toUpperCase();

    const registrarFalhaConsecutiva = (mensagem) => {
      const chave = normalizarMensagemFalha(mensagem);

      if (!chave) {
        ultimaFalhaNormalizada = "";
        falhasConsecutivas = 0;
        return false;
      }

      if (chave === ultimaFalhaNormalizada) {
        falhasConsecutivas += 1;
      } else {
        ultimaFalhaNormalizada = chave;
        falhasConsecutivas = 1;
      }

      if (falhasConsecutivas >= 3) {
        interrompido = true;
        motivoInterrupcao = `Processamento interrompido por segurança: ${falhasConsecutivas} falhas consecutivas com o mesmo retorno fiscal.`;
        return true;
      }

      return false;
    };

    const limparFalhaConsecutiva = () => {
      ultimaFalhaNormalizada = "";
      falhasConsecutivas = 0;
    };

    for (const id of idsLiberadosParaEmissao) {
      const itemAtual = mapaPreNotas.get(id) || { idPreNfe: id };

      setAutomacaoResumo((prev) => ({
        ...prev,
        mensagem: `Etapa ${prev?.faseAtual || 1}/${prev?.fasesTotal || 1}: emitindo ${resultados.length}/${idsLiberadosParaEmissao.length}`,
        atualId: id,
        atualNome: getNomePreNota(itemAtual),
      }));

      const resultado = await emitirItemLoteSilencioso(id, {
        validarPendentes: false,
      });

      resultados.push(resultado);

      const itemHistorico = montarResultadoOperacao({
        idPreNfe: id,
        item: itemAtual,
        etapa: "Emissão",
        ok: resultado.ok,
        status: resultado.resultado?.status || resultado.data?.status || (resultado.ok ? "AUTORIZADA" : "REJEITADA"),
        mensagem: resultado.mensagem || (resultado.ok ? "NFS-e autorizada." : "Falha na emissão."),
        extra: {
          numeroRps: resultado.resultado?.numeroRps || resultado.data?.numeroRps,
          numeroNfse: resultado.resultado?.numeroNfse || resultado.data?.numeroNfse,
        },
      });

      historicoEmissao.push(itemHistorico);

      const processados = resultados.length;
      const sucesso = resultados.filter((r) => r.ok).length;
      const falha = resultados.filter((r) => !r.ok).length;
      const falhasDetalhadas = [
        ...falhasValidacao,
        ...historicoEmissao.filter((item) => !item.ok),
      ];

      setAutomacaoResumo((prev) => ({
        ...prev,
        etapa: "EMISSAO",
        status: "EM_ANDAMENTO",
        mensagem: `Etapa ${prev?.faseAtual || 1}/${prev?.fasesTotal || 1}: emitindo ${processados}/${idsLiberadosParaEmissao.length}`,
        total: idsLiberadosParaEmissao.length,
        processados,
        pendentes: Math.max(0, idsLiberadosParaEmissao.length - processados),
        sucesso,
        falha,
        atualId: idsLiberadosParaEmissao[processados] || null,
        atualNome: idsLiberadosParaEmissao[processados]
          ? getNomePreNota(mapaPreNotas.get(idsLiberadosParaEmissao[processados]) || { idPreNfe: idsLiberadosParaEmissao[processados] })
          : "Finalizando emissão...",
        ultimos: historicoEmissao.slice(-6).reverse(),
        resultadosDetalhados: historicoEmissao,
        falhasDetalhadas,
      }));

      if (resultado.ok) {
        limparFalhaConsecutiva();
      } else if (registrarFalhaConsecutiva(resultado.mensagem)) {
        setAutomacaoResumo({
          etapa: "EMISSAO",
          modoFluxo: "VALIDAR_EMITIR",
          faseAtual: validarAntes && pendentesValidacao.length > 0 ? 2 : 1,
          fasesTotal: validarAntes && pendentesValidacao.length > 0 ? 2 : 1,
          status: "INTERROMPIDO",
          mostrarModal: true,
          mensagem: motivoInterrupcao,
          total: idsLiberadosParaEmissao.length,
          processados,
          pendentes: Math.max(0, idsLiberadosParaEmissao.length - processados),
          sucesso,
          falha,
          atualId: null,
          atualNome: "Processamento interrompido por segurança",
          ultimos: historicoEmissao.slice(-6).reverse(),
          resultadosDetalhados: historicoEmissao,
          falhasDetalhadas,
        });
        break;
      }
    }

    const sucesso = resultados.filter((r) => r.ok).length;
    const falhaEmissao = resultados.length - sucesso;
    const falhasDetalhadas = [
      ...falhasValidacao,
      ...historicoEmissao.filter((item) => !item.ok),
    ];
    const resumo = {
      total: idsLiberadosParaEmissao.length,
      processados: resultados.length,
      pendentes: Math.max(0, idsLiberadosParaEmissao.length - resultados.length),
      sucesso,
      falha: falhaEmissao,
      falhasValidacao: falhasValidacao.length,
      falhasDetalhadas,
      interrompido,
      motivoInterrupcao,
      resultados,
      resultadosDetalhados: historicoEmissao,
      ultimos: historicoEmissao.slice(-6).reverse(),
      startedAt,
      finishedAt: Date.now(),
    };

    await buscarPreNotas();
    await carregarConfigNfse();

    if (detalhe?.idPreNfe && idsLiberadosParaEmissao.includes(Number(detalhe.idPreNfe))) {
      await abrirDetalhe(detalhe.idPreNfe);
    }

    const totalFalhasOperacao = falhasDetalhadas.length;

    setAutomacaoResumo({
      etapa: "EMISSAO",
      modoFluxo: "VALIDAR_EMITIR",
      faseAtual: validarAntes && pendentesValidacao.length > 0 ? 2 : 1,
      fasesTotal: validarAntes && pendentesValidacao.length > 0 ? 2 : 1,
      status: interrompido ? "INTERROMPIDO" : totalFalhasOperacao > 0 ? "PARCIAL" : "CONCLUIDO",
      mostrarModal: true,
      mensagem: interrompido
        ? motivoInterrupcao
        : totalFalhasOperacao > 0
          ? `${sucesso} NFS-e autorizada(s), ${totalFalhasOperacao} falha(s) no fluxo.`
          : `${sucesso} NFS-e autorizada(s) com sucesso.`,
      atualId: null,
      atualNome: totalFalhasOperacao > 0 ? "Processamento finalizado com pendências" : "Processamento finalizado com sucesso",
      ...resumo,
    });

    if (!silencioso) {
      if (interrompido) {
        showError(motivoInterrupcao || `${sucesso} autorizada(s), ${totalFalhasOperacao} com pendência.`);
      } else if (totalFalhasOperacao > 0) {
        showError(`${sucesso} autorizada(s), ${totalFalhasOperacao} falha(s). Clique em Ver falhas para conferir.`);
        setFalhasLoteModalAberto(true);
      } else {
        showInfo(`${sucesso} NFS-e autorizada(s) com sucesso.`);
      }
    }

    setEmitindoLote(false);
    return { ...resumo, falha: totalFalhasOperacao };
  };

  const rotuloBotaoGerar = "Gerar pré-NFS-e";

  const qtdValidaveis = preNotas.filter((item) => isStatusValidavelLote(item.status)).length;
  const qtdProcessaveisLote = preNotas.filter((item) => isStatusProcessavelLote(item.status)).length;

  const falhasDetalhadasLote = useMemo(() => {
    const falhas = Array.isArray(automacaoResumo?.falhasDetalhadas)
      ? automacaoResumo.falhasDetalhadas
      : [];

    return falhas.filter((item) => item && !item.ok);
  }, [automacaoResumo]);

  const fecharProgressoLote = () => {
    setAutomacaoResumo((prev) => (prev ? { ...prev, mostrarModal: false } : prev));
  };

  const progressoOperacao = useMemo(() => {
    const resumo = automacaoResumo || {};
    const total = Number(resumo.total || 0);
    const processados = Number(
      resumo.processados !== undefined
        ? resumo.processados
        : Number(resumo.sucesso || 0) + Number(resumo.falha || 0),
    );
    const sucesso = Number(resumo.sucesso || 0);
    const falha = Number(resumo.falha || 0);
    const pendentes = Math.max(0, total - processados);
    const startedAt = Number(resumo.startedAt || 0);
    const status = normalizarStatus(resumo.status);
    const emAndamento = status === "EM_ANDAMENTO";
    const finalizado = ["CONCLUIDO", "PARCIAL", "INTERROMPIDO"].includes(status);
    const decorridoMs = startedAt
      ? Math.max(0, (emAndamento ? progressoTick : Number(resumo.finishedAt || Date.now())) - startedAt)
      : 0;
    const percentual = total > 0 ? Math.min(100, Math.round((processados / total) * 100)) : 0;
    const mediaMs = processados > 0 ? decorridoMs / processados : 0;
    const restanteMs = emAndamento && mediaMs > 0 ? mediaMs * pendentes : 0;
    const etapa = resumo.etapa || "LOTE";
    const faseAtual = Number(resumo.faseAtual || 1);
    const fasesTotal = Number(resumo.fasesTotal || 1);
    const falhasDetalhadas = Array.isArray(resumo.falhasDetalhadas) ? resumo.falhasDetalhadas : [];

    const etapaLabel = etapa === "EMISSAO"
      ? "Emissão do lote"
      : etapa === "VALIDACAO"
        ? "Validação do lote"
        : etapa === "GERACAO"
          ? "Geração de pré-NFS-e"
          : "Processamento do lote";

    const titulo = etapa === "EMISSAO"
      ? "Emitindo NFS-e validadas"
      : etapa === "VALIDACAO"
        ? "Validando pré-NFS-e"
        : etapa === "GERACAO"
          ? "Gerando pré-NFS-e"
          : "Processando lote";

    return {
      visivel: Boolean(resumo.mostrarModal) && (emAndamento || finalizado),
      emAndamento,
      finalizado,
      status,
      etapa,
      etapaLabel,
      titulo,
      faseAtual,
      fasesTotal,
      mensagem: resumo.mensagem || "Processando lote...",
      total,
      processados,
      pendentes,
      sucesso,
      falha,
      percentual,
      decorrido: startedAt ? formatarDuracaoMs(decorridoMs) : "00:00",
      restante: emAndamento
        ? (processados > 0 ? formatarDuracaoMs(restanteMs) : "calculando...")
        : "00:00",
      atualId: resumo.atualId || null,
      atualNome: resumo.atualNome || (finalizado ? "Processamento finalizado" : "Aguardando próximo item..."),
      ultimos: Array.isArray(resumo.ultimos) ? resumo.ultimos : [],
      falhasDetalhadas,
    };
  }, [automacaoResumo, progressoTick]);

  return (
    <MainLayout>
      <main className="fiscal-nfse-page">
        <section className="fiscal-nfse-hero">
          <div>
            <span className="fiscal-eyebrow">Fiscal · NFS-e</span>
            <h1>Montagem de lote de emissão</h1>
            <p>
              Monte um lote mensal por meta global de valor, priorizando clientes obrigatórios e completando
              com parcelas elegíveis até o melhor encaixe fiscal.
            </p>
          </div>

          <div className="fiscal-hero-actions">
            <button
              type="button"
              className="btn btn-light"
              onClick={buscarParcelas}
              disabled={loadingParcelas}
            >
              {loadingParcelas ? "Montando..." : "Montar lote"}
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={gerarPreNotas}
              disabled={gerando || loadingParcelas || !podeGerarPreNotas}
            >
              {gerando ? "Gerando..." : rotuloBotaoGerar}
            </button>
          </div>
        </section>

        <section className="fiscal-filter-card">
          <div className="filter-grid lote-filter-grid">
            <label>
              <span>Competência</span>
              <input
                type="month"
                value={filtros.competencia}
                onChange={(e) => updateFiltro("competencia", e.target.value)}
              />
            </label>

            <label>
              <span>Meta mínima</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={filtros.valorMin}
                onChange={(e) => updateFiltro("valorMin", e.target.value)}
              />
            </label>

            <label>
              <span>Meta máxima</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={filtros.valorMax}
                onChange={(e) => updateFiltro("valorMax", e.target.value)}
              />
            </label>

            <label>
              <span>Valor alvo</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Opcional"
                value={filtros.valorAlvo}
                onChange={(e) => updateFiltro("valorAlvo", e.target.value)}
              />
            </label>

            <label>
              <span>Situação</span>
              <select
                value={filtros.situacao}
                onChange={(e) => updateFiltro("situacao", e.target.value)}
              >
                {SITUACAO_OPTIONS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Estratégia</span>
              <select
                value={filtros.modoSelecao}
                onChange={(e) => updateFiltro("modoSelecao", e.target.value)}
              >
                {MODO_SELECAO_OPTIONS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Status pré-NFS-e</span>
              <select
                value={filtros.status}
                onChange={(e) => updateFiltro("status", e.target.value)}
              >
                {STATUS_OPTIONS.map((op) => (
                  <option key={op.value || "todos"} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="filter-search">
              <span>Busca</span>
              <input
                type="search"
                value={filtros.search}
                placeholder="Cliente, CNPJ, código ou título..."
                onChange={(e) => updateFiltro("search", e.target.value)}
              />
            </label>
          </div>

          <div className="filter-client-row">
            <div className="filter-client-select">
              <span>Cliente</span>
              <SelectCliente
                className="select-cliente"
                value={clienteSelecionado}
                onChange={setClienteSelecionado}
                isClearable
              />
            </div>

            <label className="switch-row">
              <input
                type="checkbox"
                checked={Boolean(filtros.considerarObrigatorios)}
                onChange={(e) => updateFiltro("considerarObrigatorios", e.target.checked)}
              />
              <span>Priorizar obrigatórios</span>
            </label>

            <label className="switch-row">
              <input
                type="checkbox"
                checked={Boolean(filtros.obrigatoriosSempreEntram)}
                onChange={(e) => updateFiltro("obrigatoriosSempreEntram", e.target.checked)}
              />
              <span>Obrigatórios sempre entram</span>
            </label>

            <label className="switch-row">
              <input
                type="checkbox"
                checked={Boolean(filtros.somenteObrigatorios)}
                onChange={(e) => updateFiltro("somenteObrigatorios", e.target.checked)}
              />
              <span>Somente obrigatórios</span>
            </label>

            <div className="filter-actions">
              <button type="button" className="btn btn-light" onClick={limparFiltros}>
                Limpar
              </button>

              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  buscarParcelas();
                  buscarPreNotas();
                }}
              >
                Atualizar
              </button>
            </div>
          </div>

          <div className="automation-card automation-card--two-step">
            <div className="automation-main">
              <span>Automação do lote</span>
              <strong>Fluxo em duas etapas</strong>
              <small>
                Primeiro o sistema apenas gera as pré-NFS-e do lote. Depois, na aba Pré-NFS-e, use “Validar lote” ou “Validar + emitir lote”.
              </small>
            </div>

            <div className="automation-steps">
              <div className="automation-step active">
                <span>1</span>
                <strong>Gerar pré-NFS-e</strong>
                <small>Sem validação automática nesta etapa.</small>
              </div>
              <div className="automation-step">
                <span>2</span>
                <strong>Validar e emitir</strong>
                <small>Executado pelo botão da aba Pré-NFS-e.</small>
              </div>
            </div>
          </div>

          {automacaoResumo && (
            <div className={`automation-status ${String(automacaoResumo.status || "").toLowerCase()}`}>
              <div>
                <span>
                  {automacaoResumo.etapa === "EMISSAO"
                    ? "Emissão automática"
                    : automacaoResumo.etapa === "GERACAO"
                      ? "Geração automática"
                      : "Validação automática"}
                </span>
                <strong>{automacaoResumo.mensagem}</strong>
                {normalizarStatus(automacaoResumo.status) === "EM_ANDAMENTO" && (
                  <small>Processando: {automacaoResumo.processados || 0}/{automacaoResumo.total || 0}</small>
                )}
                {falhasDetalhadasLote.length > 0 && (
                  <small>{falhasDetalhadasLote.length} falha(s) registrada(s) no último processamento.</small>
                )}
              </div>
              <div className="automation-status-actions">
                <div className="automation-status-grid">
                  <span>Total: {automacaoResumo.total || 0}</span>
                  <span>Sucesso: {automacaoResumo.sucesso || 0}</span>
                  <span>Falha: {falhasDetalhadasLote.length || automacaoResumo.falha || 0}</span>
                </div>

                {falhasDetalhadasLote.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={() => setFalhasLoteModalAberto(true)}
                  >
                    Ver falhas
                  </button>
                )}
              </div>
            </div>
          )}

          {clienteSelecionado && (
            <div className="cliente-selecionado-box full">
              <span>Cliente selecionado</span>
              <strong>{getClienteNome(clienteSelecionado)}</strong>
              <small>Código: {clienteId || "—"}</small>
            </div>
          )}
        </section>

        <section className="lote-dashboard">
          <article className="lote-panel">
            <div className="lote-panel-head">
              <div>
                <span>Meta global do lote</span>
                <strong>
                  {moedaBR(filtros.valorMin)} até {moedaBR(filtros.valorMax)}
                </strong>
              </div>
              <span className={getLoteStatusClass(loteCalculado)}>
                {manualSelection ? "Seleção manual" : getLoteStatusLabel(lote)}
              </span>
            </div>

            <div className="lote-total">
              <span>Total selecionado</span>
              <strong>{moedaBR(valorSelecionadoManual)}</strong>
              <small>
                {loteCalculado.abaixoMinimo &&
                  `Faltam ${moedaBR(loteCalculado.faltaParaMinimo)} para a meta mínima.`}
                {loteCalculado.excedeuMaximo &&
                  `Excedeu ${moedaBR(valorSelecionadoManual - Number(filtros.valorMax || 0))} do limite máximo.`}
                {loteCalculado.dentroRange &&
                  `Saldo até o teto: ${moedaBR(loteCalculado.saldoAteMaximo)}.`}
              </small>
            </div>

            <div className="lote-progress">
              <div style={{ width: `${loteCalculado.percentual}%` }} />
            </div>

            <div className="lote-metrics">
              <div>
                <span>Parcelas</span>
                <strong>{parcelasSelecionadas.length}</strong>
              </div>
              <div>
                <span>Clientes</span>
                <strong>{clientesSelecionados.length}</strong>
              </div>
              <div>
                <span>Obrigatórios</span>
                <strong>{parcelasSelecionadas.filter((p) => isObrigatorio(p)).length}</strong>
              </div>
              <div>
                <span>Valor obrigatório</span>
                <strong>{moedaBR(valorObrigatorioSelecionado)}</strong>
              </div>
            </div>

            <div className="lote-actions">
              <button
                type="button"
                className="btn btn-light"
                onClick={selecionarSugestaoAutomatica}
                disabled={!lote || loadingParcelas}
              >
                Usar sugestão automática
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={gerarPreNotas}
                disabled={gerando || !podeGerarPreNotas}
              >
                {gerando ? "Gerando..." : `${rotuloBotaoGerar} do lote`}
              </button>
            </div>
          </article>

          <article className="lote-side-kpis">
            <div className="fiscal-kpi success">
              <span>Elegíveis disponíveis</span>
              <strong>{parcelasResumo.totalElegiveis}</strong>
              <small>{moedaBR(parcelasResumo.valorElegivel)}</small>
            </div>

            <div className="fiscal-kpi mandatory">
              <span>Clientes obrigatórios</span>
              <strong>{parcelasResumo.totalObrigatorios}</strong>
              <small>{moedaBR(parcelasResumo.valorObrigatorio)}</small>
            </div>

            <div className="fiscal-kpi warning">
              <span>Pré-NFS-e</span>
              <strong>{preNotasResumo.total}</strong>
              <small>
                {statusResumo.PENDENTE || 0} pendente(s) ·{" "}
                {statusResumo.VALIDADA || 0} validada(s)
              </small>
            </div>
          </article>
        </section>

        <section className="fiscal-tabs">
          <button
            type="button"
            className={aba === "lote" ? "active" : ""}
            onClick={() => setAba("lote")}
          >
            Montagem do lote
          </button>

          <button
            type="button"
            className={aba === "preNotas" ? "active" : ""}
            onClick={() => setAba("preNotas")}
          >
            Pré-NFS-e
          </button>

          <button
            type="button"
            className={aba === "config" ? "active" : ""}
            onClick={() => setAba("config")}
          >
            Configuração
          </button>
        </section>

        {aba === "lote" && (
          <section className="fiscal-card">
            <div className="fiscal-card-head">
              <div>
                <h2>Parcelas do lote</h2>
                <p>
                  Controle a seleção global. O sistema gera uma pré-NFS-e por cliente usando apenas as parcelas marcadas.
                </p>
              </div>

              <div className="head-actions">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={buscarParcelas}
                  disabled={loadingParcelas}
                >
                  {loadingParcelas ? "Montando..." : "Recalcular lote"}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={gerarPreNotas}
                  disabled={gerando || !podeGerarPreNotas}
                >
                  {gerando ? "Gerando..." : rotuloBotaoGerar}
                </button>
              </div>
            </div>

            {lote?.message && (
              <div className={`lote-message ${loteCalculado.dentroRange ? "ok" : "warn"}`}>
                {lote.message}
              </div>
            )}

            <div className="lote-content-grid">
              <div className="lote-main-table">
                <div className="fiscal-list-toolbar">
                  <div>
                    <strong>Visualização</strong>
                    <span>
                      {tabelaParcelas.length} registro(s) · {parcelasSelecionadas.length} selecionada(s)
                    </span>
                  </div>

                  <div className="toolbar-toggles">
                    <button
                      type="button"
                      className={filtroTabela === "selecionadas" ? "toggle-filter active" : "toggle-filter"}
                      onClick={() => setFiltroTabela("selecionadas")}
                    >
                      Selecionadas
                    </button>
                    <button
                      type="button"
                      className={filtroTabela === "disponiveis" ? "toggle-filter active" : "toggle-filter"}
                      onClick={() => setFiltroTabela("disponiveis")}
                    >
                      Disponíveis
                    </button>
                    <button
                      type="button"
                      className={filtroTabela === "obrigatorias" ? "toggle-filter mandatory active" : "toggle-filter mandatory"}
                      onClick={() => setFiltroTabela("obrigatorias")}
                    >
                      Obrigatórias
                    </button>
                    <button
                      type="button"
                      className={filtroTabela === "bloqueadas" ? "toggle-filter danger active" : "toggle-filter danger"}
                      onClick={() => setFiltroTabela("bloqueadas")}
                    >
                      Bloqueadas
                    </button>
                    <button
                      type="button"
                      className={filtroTabela === "todas" ? "toggle-filter active" : "toggle-filter"}
                      onClick={() => setFiltroTabela("todas")}
                    >
                      Todas
                    </button>
                  </div>
                </div>

                <div className="table-wrap">
                  <table className="fiscal-table lote-table">
                    <thead>
                      <tr>
                        <th className="check-col">Sel.</th>
                        <th>Ordem</th>
                        <th>Cliente</th>
                        <th>CNPJ/CPF</th>
                        <th>Título</th>
                        <th>Vencimento</th>
                        <th className="t-right">Valor</th>
                        <th>Regra</th>
                        <th>Motivo</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loadingParcelas && (
                        <tr>
                          <td colSpan="9" className="empty-cell">
                            Montando lote...
                          </td>
                        </tr>
                      )}

                      {!loadingParcelas && tabelaParcelas.length === 0 && (
                        <tr>
                          <td colSpan="9" className="empty-cell">
                            Nenhuma parcela para exibir. Clique em “Montar lote”.
                          </td>
                        </tr>
                      )}

                      {!loadingParcelas &&
                        tabelaParcelas.map((parcela) => {
                          const tituloId = Number(parcela.tituloId);
                          const selecionada = selectedTituloIds.has(tituloId);
                          const obrigatoria = isObrigatorio(parcela);

                          return (
                            <tr
                              key={`${parcela.tituloId}-${parcela.clienteId}`}
                              className={selecionada ? "row-selected" : ""}
                            >
                              <td className="check-col">
                                <input
                                  type="checkbox"
                                  checked={selecionada}
                                  disabled={!parcela.elegivel}
                                  onChange={() => toggleTituloLote(parcela)}
                                />
                              </td>
                              <td>
                                <strong>{selecionada ? parcela.ordemLote || "Manual" : "—"}</strong>
                              </td>
                              <td>
                                <strong>{parcela.cliente || parcela.razaoSocial || "—"}</strong>
                                <small>Cód. {parcela.clienteId || "—"}</small>
                              </td>
                              <td>{parcela.cnpj || "—"}</td>
                              <td>
                                <strong>{parcela.numTitulo || "—"}</strong>
                                <small>Parcela {parcela.parcela || "—"}</small>
                              </td>
                              <td>{dataBR(parcela.vencimento)}</td>
                              <td className="t-right">
                                <strong>{moedaBR(parcela.valor)}</strong>
                              </td>
                              <td>
                                <span className={getRegraClass(parcela)}>
                                  {obrigatoria ? "Obrigatório" : getRegraLabel(parcela)}
                                </span>
                              </td>
                              <td>
                                {parcela.motivoBloqueio ? (
                                  <span className="text-danger">{parcela.motivoBloqueio}</span>
                                ) : (
                                  <span>{getMotivoLoteLabel(parcela.motivoSelecaoLote)}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>

              <aside className="lote-clientes-panel">
                <div className="panel-title">
                  <h3>Prévia por cliente</h3>
                  <span>{clientesSelecionados.length} cliente(s)</span>
                </div>

                <div className="cliente-preview-list">
                  {clientesSelecionados.length === 0 && (
                    <div className="empty-state compact">
                      Nenhum cliente selecionado no lote.
                    </div>
                  )}

                  {clientesSelecionados.map((cliente) => (
                    <article
                      key={cliente.clienteId}
                      className={cliente.obrigatorio ? "cliente-preview mandatory" : "cliente-preview"}
                    >
                      <div>
                        <strong>{cliente.cliente || cliente.razaoSocial}</strong>
                        <small>Cód. {cliente.clienteId} · {cliente.qtdParcelas} parcela(s)</small>
                      </div>
                      <span>{moedaBR(cliente.valorTotal)}</span>
                    </article>
                  ))}
                </div>
              </aside>
            </div>
          </section>
        )}

        {aba === "preNotas" && (
          <section className="fiscal-card">
            <div className="fiscal-card-head">
              <div>
                <h2>Pré-NFS-e geradas</h2>
                <p>
                  Fluxo automatizado: valide o lote completo e emita somente as pré-NFS-e aprovadas.
                </p>
              </div>

              <div className="head-actions pre-nota-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => validarLotePreNotas()}
                  disabled={validandoLote || loadingPreNotas || qtdValidaveis === 0}
                  title={qtdValidaveis === 0 ? "Nenhuma pré-NFS-e pendente para validar" : "Validar pré-NFS-e pendentes em lote"}
                >
                  {validandoLote ? "Validando lote..." : `Validar lote (${qtdValidaveis})`}
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => emitirLotePreNotas({ validarAntes: true })}
                  disabled={emitindoLote || validandoLote || loadingPreNotas || qtdProcessaveisLote === 0}
                  title="Valida pendentes e emite somente as pré-NFS-e aprovadas"
                >
                  {emitindoLote
                    ? "Emitindo lote..."
                    : `Validar + emitir lote (${qtdProcessaveisLote})`}
                </button>

                <button
                  type="button"
                  className="btn btn-light"
                  onClick={buscarPreNotas}
                  disabled={loadingPreNotas}
                >
                  {loadingPreNotas ? "Atualizando..." : "Atualizar lista"}
                </button>
              </div>
            </div>

            <div className="table-wrap">
              <table className="fiscal-table pre-nota-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>CNPJ/CPF</th>
                    <th>Competência</th>
                    <th className="t-right">Valor</th>
                    <th>Parcelas</th>
                    <th>Status</th>
                    <th>Regra</th>
                    <th>Atualização</th>
                    <th>Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {loadingPreNotas && (
                    <tr>
                      <td colSpan="10" className="empty-cell">
                        Carregando pré-NFS-e...
                      </td>
                    </tr>
                  )}

                  {!loadingPreNotas && preNotasFiltradas.length === 0 && (
                    <tr>
                      <td colSpan="10" className="empty-cell">
                        Nenhuma pré-NFS-e encontrada para os filtros.
                      </td>
                    </tr>
                  )}

                  {!loadingPreNotas &&
                    preNotasFiltradas.map((item) => (
                      <tr key={item.idPreNfe || item.idPreNfse}>
                        <td>
                          <strong>#{item.idPreNfe || item.idPreNfse}</strong>
                        </td>
                        <td>
                          <strong>{item.cliente || item.razaoSocial || "—"}</strong>
                          <small>Cód. {item.codCliente || item.nrLicenca || "—"}</small>
                          {item.mensagemStatus && (
                            <small className="status-message">{item.mensagemStatus}</small>
                          )}
                        </td>
                        <td>{item.cnpj || "—"}</td>
                        <td>{item.competencia || "—"}</td>
                        <td className="t-right">
                          <strong>{moedaBR(item.valorTotal || item.valorSelecionado)}</strong>
                        </td>
                        <td>
                          <strong>{item.qtdSelecionadas || item.qtdParcelas || 0}</strong>
                        </td>
                        <td>
                          <span className={getStatusClass(item.status)}>
                            {getStatusLabel(item.status)}
                          </span>
                        </td>
                        <td>
                          <span className={getRegraClass(item)}>
                            {isObrigatorio(item) ? "Obrigatório" : "Lote"}
                          </span>
                        </td>
                        <td>{dataHoraBR(item.atualizadoEm || item.criadoEm)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={() => abrirDetalhe(item.idPreNfe || item.idPreNfse)}
                          >
                            Abrir
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {aba === "config" && (
          <section className="fiscal-card config-card">
            <div className="fiscal-card-head config-head">
              <div>
                <h2>Configuração NFS-e</h2>
                <p>
                  Parâmetros do prestador, certificado, RPS e dados de serviço usados na emissão.
                </p>
              </div>

              <div className="config-actions">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={carregarConfigNfse}
                  disabled={loadingConfig}
                >
                  {loadingConfig ? "Carregando..." : "Recarregar"}
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => salvarConfigNfse()}
                  disabled={salvandoConfig}
                >
                  {salvandoConfig ? "Salvando..." : "Salvar configuração"}
                </button>
              </div>
            </div>

            <div className="config-alert">
              <strong>Governança fiscal</strong>
              <span>
                Alterações em ambiente, RPS, prestador e certificado impactam diretamente a emissão fiscal.
                Valide os dados antes de salvar.
              </span>
            </div>

            <div
              className={
                String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"
                  ? "nfse-env-card nfse-env-card--prod"
                  : "nfse-env-card nfse-env-card--hom"
              }
            >
              <div className="nfse-env-main">
                <span className="nfse-env-label">Ambiente de emissão</span>
                <strong
                  className={
                    String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"
                      ? "nfse-env-badge nfse-env-badge--prod"
                      : "nfse-env-badge nfse-env-badge--hom"
                  }
                >
                  {String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"
                    ? "PRODUÇÃO"
                    : "HOMOLOGAÇÃO"}
                </strong>
                <p>
                  {String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"
                    ? "Produção ativa: as próximas emissões transmitidas terão valor fiscal real."
                    : "Homologação ativa: emissões de teste, sem valor fiscal."}
                </p>
              </div>

              <div className="nfse-env-switch-wrap">
                <span>Homologação</span>
                <label className="nfse-env-switch" title="Alternar ambiente NFS-e">
                  <input
                    type="checkbox"
                    checked={String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"}
                    disabled={salvandoConfig || loadingConfig}
                    onChange={(event) =>
                      alterarAmbienteNfse(event.target.checked ? "PRODUCAO" : "HOMOLOGACAO")
                    }
                  />
                  <span className="nfse-env-switch-slider" />
                </label>
                <span>Produção</span>
              </div>

              {String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO" && (
                <div className="nfse-env-warning">
                  Atenção: qualquer emissão transmitida neste ambiente possui validade fiscal.
                </div>
              )}
            </div>

            <div className="config-top-grid">
              <label>
                <span>NR Licença</span>
                <input
                  value={configBusca.nrLicenca}
                  onChange={(e) => atualizarConfigBusca("nrLicenca", e.target.value)}
                />
              </label>

              <label>
                <span>ID Empresa</span>
                <input
                  type="number"
                  min="1"
                  value={configBusca.idEmpresa}
                  onChange={(e) => atualizarConfigBusca("idEmpresa", e.target.value)}
                />
              </label>

              <button
                type="button"
                className="btn btn-outline"
                onClick={carregarConfigNfse}
                disabled={loadingConfig}
              >
                Buscar configuração
              </button>

              <div className={getAmbienteClass(configNfse.ambiente)}>
                Ambiente atual: <strong>{configNfse.ambiente}</strong>
              </div>
            </div>

            <div className="config-summary-grid">
              <article>
                <span>Certificado</span>
                <strong>{configNfse.certificadoId || "Não vinculado"}</strong>
                <small>{configNfse.certificadoNome || "—"}</small>
              </article>

              <article>
                <span>Validade</span>
                <strong>{dataBR(configNfse.certificadoValidade)}</strong>
                <small>{configNfse.certificadoVinculado ? "Vinculado" : "Sem vínculo"}</small>
              </article>

              <article>
                <span>Próximo RPS</span>
                <strong>{configNfse.proximoNumeroRps || "—"}</strong>
                <small>Série {configNfse.serieRps || "—"}</small>
              </article>

              <article>
                <span>Alíquota ISS</span>
                <strong>{decimalBR(configNfse.aliquotaIss)}%</strong>
                <small>{configNfse.optanteSimplesNacional ? "Simples Nacional" : "Regime normal"}</small>
              </article>
            </div>

            <div className="config-form">
              <section className="config-section">
                <div className="config-section-title">
                  <h3>Ambiente e endpoints</h3>
                  <p>Controle de transmissão para homologação ou produção.</p>
                </div>

                <div className="config-form-grid">
                  <div className="config-env-inline span-3">
                    <div>
                      <span>Ambiente ativo</span>
                      <strong>
                        {String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"
                          ? "Produção"
                          : "Homologação"}
                      </strong>
                      <small>
                        Use o switch superior para alternar o ambiente com confirmação operacional.
                      </small>
                    </div>

                    <button
                      type="button"
                      className={
                        String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"
                          ? "btn btn-light"
                          : "btn btn-danger-soft"
                      }
                      onClick={() =>
                        alterarAmbienteNfse(
                          String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"
                            ? "HOMOLOGACAO"
                            : "PRODUCAO",
                        )
                      }
                      disabled={salvandoConfig || loadingConfig}
                    >
                      {String(configNfse.ambiente || "").toUpperCase() === "PRODUCAO"
                        ? "Voltar para homologação"
                        : "Ativar produção"}
                    </button>
                  </div>

                  <label className="span-2">
                    <span>Endpoint produção</span>
                    <input
                      value={configNfse.endpointProducao}
                      onChange={(e) => atualizarConfigCampo("endpointProducao", e.target.value)}
                    />
                  </label>

                  <label className="span-2">
                    <span>Endpoint homologação</span>
                    <input
                      value={configNfse.endpointHomologacao}
                      onChange={(e) => atualizarConfigCampo("endpointHomologacao", e.target.value)}
                    />
                  </label>
                </div>
              </section>

              <section className="config-section">
                <div className="config-section-title">
                  <h3>Prestador</h3>
                  <p>Dados cadastrais do emitente no NFe-cidades.</p>
                </div>

                <div className="config-form-grid">
                  <label>
                    <span>CNPJ prestador</span>
                    <input
                      value={configNfse.cnpjPrestador}
                      maxLength={14}
                      onChange={(e) => atualizarConfigCampo("cnpjPrestador", onlyNumbers(e.target.value))}
                    />
                  </label>

                  <label>
                    <span>Inscrição municipal</span>
                    <input
                      value={configNfse.inscricaoMunicipal}
                      onChange={(e) => atualizarConfigCampo("inscricaoMunicipal", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Código mobiliário</span>
                    <input
                      value={configNfse.codigoMobiliario}
                      onChange={(e) => atualizarConfigCampo("codigoMobiliario", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Município IBGE</span>
                    <input
                      value={configNfse.municipioIbge}
                      onChange={(e) => atualizarConfigCampo("municipioIbge", onlyNumbers(e.target.value))}
                    />
                  </label>

                  <label>
                    <span>Município nome</span>
                    <input
                      value={configNfse.municipioNome}
                      onChange={(e) => atualizarConfigCampo("municipioNome", e.target.value)}
                    />
                  </label>
                </div>
              </section>

              <section className="config-section">
                <div className="config-section-title">
                  <h3>RPS e serviço</h3>
                  <p>Sequência, serviço, NBS e tributação fiscal.</p>
                </div>

                <div className="config-form-grid">
                  <label>
                    <span>Série RPS</span>
                    <input
                      value={configNfse.serieRps}
                      onChange={(e) => atualizarConfigCampo("serieRps", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Tipo RPS</span>
                    <input
                      type="number"
                      min="1"
                      value={configNfse.tipoRps}
                      onChange={(e) => atualizarConfigCampo("tipoRps", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Próximo número RPS</span>
                    <input
                      type="number"
                      min="1"
                      value={configNfse.proximoNumeroRps}
                      onChange={(e) => atualizarConfigCampo("proximoNumeroRps", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Item lista serviço</span>
                    <input
                      value={configNfse.itemListaServico}
                      onChange={(e) => atualizarConfigCampo("itemListaServico", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Código NBS</span>
                    <input
                      value={configNfse.codigoNbs}
                      onChange={(e) => atualizarConfigCampo("codigoNbs", onlyNumbers(e.target.value))}
                    />
                  </label>

                  <label>
                    <span>Código CNAE</span>
                    <input
                      value={configNfse.codigoCnae}
                      onChange={(e) => atualizarConfigCampo("codigoCnae", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Cód. tributação município</span>
                    <input
                      value={configNfse.codigoTributacaoMunicipio}
                      onChange={(e) => atualizarConfigCampo("codigoTributacaoMunicipio", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Exigibilidade ISS</span>
                    <input
                      type="number"
                      min="1"
                      value={configNfse.exigibilidadeIss}
                      onChange={(e) => atualizarConfigCampo("exigibilidadeIss", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Alíquota ISS %</span>
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={configNfse.aliquotaIss}
                      onChange={(e) => atualizarConfigCampo("aliquotaIss", e.target.value)}
                    />
                  </label>
                </div>

                <div className="config-switch-grid">
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(configNfse.issRetido)}
                      onChange={(e) => atualizarConfigCampo("issRetido", e.target.checked)}
                    />
                    <span>ISS retido</span>
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(configNfse.optanteSimplesNacional)}
                      onChange={(e) => atualizarConfigCampo("optanteSimplesNacional", e.target.checked)}
                    />
                    <span>Optante Simples Nacional</span>
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(configNfse.incentivoFiscal)}
                      onChange={(e) => atualizarConfigCampo("incentivoFiscal", e.target.checked)}
                    />
                    <span>Incentivo fiscal</span>
                  </label>
                </div>
              </section>

              <section className="config-section">
                <div className="config-section-title">
                  <h3>Discriminação e e-mails</h3>
                  <p>Texto padrão da nota e rotinas de envio.</p>
                </div>

                <div className="config-form-grid">
                  <label className="span-3">
                    <span>Discriminação padrão</span>
                    <textarea
                      rows="4"
                      value={configNfse.discriminacaoPadrao}
                      onChange={(e) => atualizarConfigCampo("discriminacaoPadrao", e.target.value)}
                    />
                  </label>

                  <label className="span-3">
                    <span>Outras informações</span>
                    <textarea
                      rows="3"
                      value={configNfse.outrasInformacoes}
                      onChange={(e) => atualizarConfigCampo("outrasInformacoes", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>E-mail relatório interno</span>
                    <input
                      value={configNfse.emailRelatorioInterno}
                      onChange={(e) => atualizarConfigCampo("emailRelatorioInterno", e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Certificado ID</span>
                    <input
                      type="number"
                      min="1"
                      value={configNfse.certificadoId}
                      onChange={(e) => atualizarConfigCampo("certificadoId", e.target.value)}
                    />
                  </label>
                </div>

                <div className="config-switch-grid">
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(configNfse.enviarEmailCliente)}
                      onChange={(e) => atualizarConfigCampo("enviarEmailCliente", e.target.checked)}
                    />
                    <span>Enviar e-mail ao cliente</span>
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(configNfse.enviarRelatorioInterno)}
                      onChange={(e) => atualizarConfigCampo("enviarRelatorioInterno", e.target.checked)}
                    />
                    <span>Enviar relatório interno</span>
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(configNfse.ativo)}
                      onChange={(e) => atualizarConfigCampo("ativo", e.target.checked)}
                    />
                    <span>Configuração ativa</span>
                  </label>
                </div>
              </section>
            </div>

            <div className="config-footer-actions">
              <button
                type="button"
                className="btn btn-light"
                onClick={carregarConfigNfse}
                disabled={loadingConfig}
              >
                Descartar alterações
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => salvarConfigNfse()}
                disabled={salvandoConfig}
              >
                {salvandoConfig ? "Salvando..." : "Salvar configuração"}
              </button>
            </div>
          </section>
        )}

        {confirmacaoModal && (
          <div className="nfse-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="nfse-confirm-title">
            <div className={`nfse-confirm-modal nfse-confirm-modal--${confirmacaoModal.tipo || "warning"}`}>
              <div className="nfse-confirm-icon" aria-hidden="true">
                {confirmacaoModal.tipo === "danger" ? "!" : confirmacaoModal.tipo === "info" ? "i" : "✓"}
              </div>

              <div className="nfse-confirm-content">
                <span>{confirmacaoModal.subtitulo}</span>
                <h2 id="nfse-confirm-title">{confirmacaoModal.titulo}</h2>
                <p>{confirmacaoModal.descricao}</p>

                {confirmacaoModal.alerta && (
                  <div className="nfse-confirm-alert">
                    {confirmacaoModal.alerta}
                  </div>
                )}

                {confirmacaoModal.textoObrigatorio && (
                  <label className="nfse-confirm-input">
                    <span>Confirmação obrigatória</span>
                    <strong>Digite {confirmacaoModal.textoObrigatorio} para continuar</strong>
                    <input
                      type="text"
                      value={confirmacaoModal.valorDigitado || ""}
                      onChange={(event) => atualizarTextoConfirmacao(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          confirmarModalOperacao();
                        }
                      }}
                      autoFocus
                    />
                    {confirmacaoModal.erro && <small>{confirmacaoModal.erro}</small>}
                  </label>
                )}

                <div className="nfse-confirm-actions">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => fecharConfirmacaoOperacao(false)}
                  >
                    {confirmacaoModal.cancelarLabel || "Cancelar"}
                  </button>

                  <button
                    type="button"
                    className={confirmacaoModal.tipo === "danger" ? "btn btn-danger" : "btn btn-primary"}
                    onClick={confirmarModalOperacao}
                  >
                    {confirmacaoModal.confirmarLabel || "Confirmar"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {progressoOperacao.visivel && (
          <div className="lote-processing-overlay" role="dialog" aria-modal="true" aria-live="polite">
            <div className={`lote-processing-modal ${progressoOperacao.finalizado ? "finished" : ""}`}>
              <div className="lote-processing-header">
                <div>
                  <span>
                    {progressoOperacao.fasesTotal > 1
                      ? `Etapa ${progressoOperacao.faseAtual}/${progressoOperacao.fasesTotal} · ${progressoOperacao.etapaLabel}`
                      : progressoOperacao.etapaLabel}
                  </span>
                  <h2>{progressoOperacao.titulo}</h2>
                  <p>{progressoOperacao.mensagem}</p>
                </div>

                <div className="lote-processing-percent">
                  <strong>{progressoOperacao.percentual}%</strong>
                  {progressoOperacao.finalizado && (
                    <button
                      type="button"
                      className="btn-close-processing"
                      onClick={fecharProgressoLote}
                      aria-label="Fechar acompanhamento do lote"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              <div className="lote-processing-bar">
                <div style={{ width: `${progressoOperacao.percentual}%` }} />
              </div>

              {progressoOperacao.fasesTotal > 1 && (
                <div className="lote-processing-steps">
                  <div className={progressoOperacao.faseAtual >= 1 ? "active" : ""}>
                    <span>1</span>
                    <strong>Validação</strong>
                  </div>
                  <div className={progressoOperacao.faseAtual >= 2 ? "active" : ""}>
                    <span>2</span>
                    <strong>Emissão</strong>
                  </div>
                </div>
              )}

              <div className="lote-processing-grid">
                <div>
                  <span>Processadas</span>
                  <strong>{progressoOperacao.processados}/{progressoOperacao.total}</strong>
                </div>
                <div>
                  <span>
                    {progressoOperacao.etapa === "EMISSAO"
                      ? "Autorizadas"
                      : progressoOperacao.etapa === "GERACAO"
                        ? "Geradas"
                        : "Validadas"}
                  </span>
                  <strong>{progressoOperacao.sucesso}</strong>
                </div>
                <div>
                  <span>Falhas</span>
                  <strong>{progressoOperacao.falhasDetalhadas.length || progressoOperacao.falha}</strong>
                </div>
                <div>
                  <span>Pendentes</span>
                  <strong>{progressoOperacao.pendentes}</strong>
                </div>
                <div>
                  <span>Decorrido</span>
                  <strong>{progressoOperacao.decorrido}</strong>
                </div>
                <div>
                  <span>Estimativa restante</span>
                  <strong>{progressoOperacao.restante}</strong>
                </div>
              </div>

              <div className="lote-processing-current">
                <span>{progressoOperacao.finalizado ? "Resultado" : "Item atual"}</span>
                <strong>
                  {progressoOperacao.atualId ? `#${progressoOperacao.atualId} · ` : ""}
                  {progressoOperacao.atualNome}
                </strong>
              </div>

              <div className="lote-processing-history">
                <div className="lote-processing-history-head">
                  <span>Últimos retornos</span>
                  <small>Atualizado em tempo real</small>
                </div>

                {progressoOperacao.ultimos.length === 0 ? (
                  <p>Nenhum item concluído ainda.</p>
                ) : (
                  progressoOperacao.ultimos.map((item) => (
                    <div
                      key={`${item.etapa || "item"}-${item.idPreNfe}-${item.mensagem}`}
                      className={item.ok ? "history-item ok" : "history-item fail"}
                    >
                      <strong>#{item.idPreNfe} · {item.nome}</strong>
                      <span>{item.mensagem}</span>
                    </div>
                  ))
                )}
              </div>

              <div className={progressoOperacao.finalizado ? "lote-processing-footer done" : "lote-processing-warning"}>
                {progressoOperacao.finalizado ? (
                  <>
                    <span>Processamento finalizado.</span>
                    <div>
                      {progressoOperacao.falhasDetalhadas.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => setFalhasLoteModalAberto(true)}
                        >
                          Ver falhas ({progressoOperacao.falhasDetalhadas.length})
                        </button>
                      )}
                      <button type="button" className="btn btn-primary btn-sm" onClick={fecharProgressoLote}>
                        Fechar
                      </button>
                    </div>
                  </>
                ) : (
                  "Não feche esta tela nem atualize o navegador até o processamento finalizar."
                )}
              </div>
            </div>
          </div>
        )}

        {falhasLoteModalAberto && falhasDetalhadasLote.length > 0 && (
          <div className="lote-failures-overlay" role="dialog" aria-modal="true">
            <div className="lote-failures-modal">
              <div className="lote-failures-head">
                <div>
                  <span>Relatório do lote</span>
                  <h2>Pré-NFS-e com falha</h2>
                  <p>Use esta lista para identificar os clientes que precisam de correção antes de reenviar.</p>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setFalhasLoteModalAberto(false)}
                >
                  ×
                </button>
              </div>

              <div className="lote-failures-list">
                {falhasDetalhadasLote.map((item) => (
                  <article key={`${item.etapa}-${item.idPreNfe}-${item.mensagem}`} className="lote-failure-item">
                    <div>
                      <span>{item.etapa || "Processamento"}</span>
                      <strong>#{item.idPreNfe} · {item.nome}</strong>
                      <p>{item.mensagem}</p>
                      <small>
                        {item.codCliente ? `Cliente: ${item.codCliente}` : ""}
                        {item.cnpj ? ` · CNPJ/CPF: ${item.cnpj}` : ""}
                        {item.status ? ` · Status: ${getStatusLabel(item.status)}` : ""}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn btn-light btn-sm"
                      onClick={() => {
                        setFalhasLoteModalAberto(false);
                        abrirDetalhe(item.idPreNfe);
                      }}
                    >
                      Abrir
                    </button>
                  </article>
                ))}
              </div>
            </div>
          </div>
        )}

        {detalhe && (
          <div className="fiscal-modal-backdrop">
            <div className={`fiscal-modal ${detalheSomenteLeitura ? "readonly" : ""}`}>
              <div className="fiscal-modal-head">
                <div>
                  <span>Pré-NFS-e #{detalhe.idPreNfe || detalhe.idPreNfse}</span>
                  <h2>{detalhe.cliente || detalhe.razaoSocial || "Cliente"}</h2>
                  <p>
                    Competência {detalhe.competencia || "—"} · Status{" "}
                    {getStatusLabel(detalhe.status)}
                  </p>
                </div>

                <button type="button" className="btn-close" onClick={fecharDetalhe}>
                  ×
                </button>
              </div>

              {loadingDetalhe ? (
                <div className="empty-state">Carregando detalhe...</div>
              ) : (
                <>
                  {detalheAutorizada && (
                    <div className="nfse-authorized-banner">
                      <div>
                        <strong>NFS-e autorizada com sucesso.</strong>
                        <span>Registro fiscal bloqueado para edição e reemissão.</span>
                      </div>

                      <div className="nfse-authorized-data">
                        <span>Nº NFS-e</span>
                        <strong>{numeroNfseDetalhe || "—"}</strong>
                      </div>

                      <div className="nfse-authorized-data wide">
                        <span>Código de verificação</span>
                        <strong>{codigoVerificacaoDetalhe || "—"}</strong>
                      </div>
                    </div>
                  )}

                  {detalheCancelada && (
                    <div className="nfse-readonly-banner warn">
                      <strong>Pré-NFS-e cancelada/excluída.</strong>
                      <span>Registro somente leitura para auditoria.</span>
                    </div>
                  )}

                  <div className="modal-summary-grid">
                    <div>
                      <span>Valor atual</span>
                      <strong>{moedaBR(detalhe.valorTotal)}</strong>
                    </div>
                    <div>
                      <span>Selecionado</span>
                      <strong>{moedaBR(valorSelecionadoDetalhe)}</strong>
                    </div>
                    <div>
                      <span>Parcelas</span>
                      <strong>{(detalhe.parcelas || []).length}</strong>
                    </div>
                    <div>
                      <span>Status</span>
                      <strong>{getStatusLabel(detalhe.status)}</strong>
                    </div>
                  </div>

                  <div className="cliente-fiscal-box">
                    <div>
                      <span>CNPJ/CPF</span>
                      <strong>{detalhe.cnpj || "—"}</strong>
                    </div>
                    <div>
                      <span>Código cliente</span>
                      <strong>{detalhe.codCliente || detalhe.nrLicenca || "—"}</strong>
                    </div>
                    <div>
                      <span>Endereço</span>
                      <strong>
                        {[detalhe.endereco, detalhe.numero, detalhe.bairro, detalhe.cidade, detalhe.uf]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </strong>
                    </div>
                    <div>
                      <span>CEP / IBGE</span>
                      <strong>
                        {detalhe.cep || "—"} · {detalhe.codMun || "—"}
                      </strong>
                    </div>
                  </div>

                  {detalhe.mensagemStatus && (
                    <div
                      className={
                        ["VALIDADA", "AUTORIZADA"].includes(String(detalhe.status || "").toUpperCase())
                          ? "validation-message ok"
                          : "validation-message warn"
                      }
                    >
                      {detalhe.mensagemStatus}
                    </div>
                  )}

                  <div className="parcelas-modal-head">
                    <div>
                      <h3>Parcelas da pré-NFS-e</h3>
                      <p>
                        {detalheSomenteLeitura
                          ? "Registro autorizado/cancelado. As parcelas ficam bloqueadas para auditoria."
                          : "Alterar parcelas retorna a pré-nota para pendente e exige nova validação."}
                      </p>
                    </div>

                    <strong>{moedaBR(valorSelecionadoDetalhe)}</strong>
                  </div>

                  <div className="table-wrap">
                    <table className="fiscal-table compact">
                      <thead>
                        <tr>
                          <th>Sel.</th>
                          <th>Título</th>
                          <th>Parcela</th>
                          <th>Vencimento</th>
                          <th className="t-right">Valor</th>
                          <th>Status</th>
                          <th>Regra</th>
                        </tr>
                      </thead>

                      <tbody>
                        {(detalhe.parcelas || []).map((parcela) => (
                          <tr
                            key={parcela.idPreNfeParcela || parcela.tituloId}
                            className={detalheSomenteLeitura ? "row-readonly" : ""}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selecionadasDetalhe.has(Number(parcela.tituloId))}
                                disabled={detalheSomenteLeitura}
                                title={detalheSomenteLeitura ? "Pré-NFS-e somente leitura" : "Selecionar/remover parcela"}
                                onChange={() => toggleParcelaDetalhe(parcela.tituloId)}
                              />
                            </td>
                            <td>{parcela.numTitulo || parcela.tituloId}</td>
                            <td>{parcela.parcela || "—"}</td>
                            <td>{dataBR(parcela.vencimento)}</td>
                            <td className="t-right">
                              <strong>{moedaBR(parcela.valor)}</strong>
                            </td>
                            <td>{parcela.status || "—"}</td>
                            <td>
                              <span className={getRegraClass(parcela)}>
                                {getRegraLabel(parcela)}
                              </span>
                            </td>
                          </tr>
                        ))}

                        {(detalhe.parcelas || []).length === 0 && (
                          <tr>
                            <td colSpan="7" className="empty-cell">
                              Nenhuma parcela vinculada.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="fiscal-modal-actions">
                    {detalheSomenteLeitura ? (
                      <>
                        <span className="readonly-note">
                          {detalheAutorizada
                            ? "Documento autorizado. Edição e reemissão bloqueadas."
                            : "Registro somente leitura."}
                        </span>

                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={fecharDetalhe}
                        >
                          Fechar
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={salvarParcelasDetalhe}
                          disabled={salvandoParcelas || !podeEditarDetalhe}
                        >
                          {salvandoParcelas ? "Salvando..." : "Salvar parcelas"}
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => validarPreNota(detalhe.idPreNfe)}
                          disabled={validando || validandoLote || !podeValidarDetalhe}
                        >
                          {validando ? "Validando..." : "Validar"}
                        </button>

                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => emitirPreNota(detalhe.idPreNfe)}
                          disabled={emitindo || emitindoLote || !podeEmitirDetalhe}
                        >
                          {emitindo ? "Emitindo..." : "Emitir NFS-e"}
                        </button>
                      </>
                    )}
                  </div>

                  <div className="audit-row">
                    <span>Criado em: {dataHoraBR(detalhe.criadoEm)}</span>
                    <span>Atualizado em: {dataHoraBR(detalhe.atualizadoEm)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </MainLayout>
  );
};

export default FiscalNfse;
